import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiFileText, FiPlay, FiPlus, FiSearch, FiTrash2, FiX } from 'react-icons/fi';
import { PiHandPalmBold } from 'react-icons/pi';
import { API_URLS, getPlayerImageUrl, getTeamImageUrl } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { IfCanWrite, IfCanWriteMatchMedia, useAdminAccess } from '../utilities/adminAccess';
import { champFields, extractEntity, extractList } from '../utilities/tournamentsApi';
import { resetMobileViewport } from '../utilities/mobileViewport';
import {
  ALL_STAFF_SLOTS,
  REFEREE_SLOT_OPTIONS,
  ROLE_SLOT_GROUPS,
  assignmentTarget,
  assignPersonToMatch,
  extractAdminStaff,
  isRefereeRole,
  matchToSavePayload,
  normalizeStaffMatch,
  refereeSlotLabelKey,
  staffDisplayName,
  staffPhotoUrl,
  staffRequestBody,
  teamCardName,
} from '../utilities/matchStaffApi';
import { roleLabel } from '../utilities/usersApi';
import {
  MATCH_EVENT_TYPES,
  MATCH_PLAY_OPTIONS,
  VIDEO_TYPES,
  buildApproveCaptainPayload,
  buildMatchApplicationPayload,
  buildProtocolRosterEvents,
  eventIcon,
  eventIconMeta,
  eventLabel,
  eventCommentText,
  eventTypeById,
  MATCH_SERVICE_EVENT_IDS,
  MAIN_TIMELINE_EVENT_IDS,
  YELLOW_CARD_EVENT_ID,
  SECOND_YELLOW_CARD_EVENT_ID,
  resolveYellowCardEventId,
  extractLineups,
  extractMatch,
  extractMatchStatistics,
  extractPhotoSetPayload,
  extractRoster,
  extractRosterEvents,
  isMatchKeeper,
  isMatchServiceEvent,
  MATCH_KEEPER_POSITION,
  matchHasUnapprovedCaptainLineup,
  teamHasUnapprovedCaptainLineup,
  loadMatchEvents,
  matchClock,
  matchPlayPhase,
  matchRequestBody,
  matchStatusKey,
  openMatchProtocol,
  finishPlayCondition,
  playConditionForPhase,
  playPhaseSelectValue,
  playerDisplayName,
  playerShirtNumber,
  playerShortName,
  playerEventParts,
  teamAbbrev,
  positionLabel,
  formatEventMinuteLabel,
  toEventTime,
  withMatchTimelineEvents,
  estimateLiveEventClock,
  estimatePlayoffExpectedSlots,
  formatPlayoffNetPositionLabel,
  isPlayoffStage,
  playoffNetPositionPairingHint,
} from '../utilities/matchesApi';
import { kickoffFieldsForApi, normalizePartialKickoffTime, resolveMatchTimezone } from '../utilities/matchKickoff';
import useComponentVisible from '../utilities/useComponentVisible';
import MatchFormationPreview from '../components/MatchFormationPreview';
import MatchLineupSetup from '../components/MatchLineupSetup';
import {
  emptyLineupSetup,
  lineupToAssignments,
  loadLineupSetup,
  saveLineupSetup,
  setupFromRoster,
  setupFromServer,
} from '../utilities/lineupSetup';
import StadiumCrumbs from '../components/StadiumCrumbs';
import CustomModal from '../components/CustomModal';
import OnlineMatch from './OnlineMatch';
import OnlineClockBar, { OnlineClockNowButton } from '../components/OnlineClockBar';
import {
  adoptServerClock,
  clockMatchesServer,
  clockSeconds,
  hintedTimeNumber,
  isClockLive,
  saveOnlineMatch,
  splitSeconds,
  statusForPhase,
  timeNumberForStatus,
  useOnlineClock,
} from '../utilities/onlineMatchStore';
import defaultTeamLogo from '../images/defaultTeamLogo.png';
import defaultAvatar from '../images/defaultAvatar.png';
import '../styles/createMatchModal.css';
import '../styles/stadiumsManagement.css';
import '../styles/gamesManagement.css';

const emptyEventForm = {
  event_id: 1,
  team_id: '',
  player_id: '',
  additional_player_id: '',
  minute: '',
  second: '',
  comment: '',
};

/** Быстрые события + хоткеи по event.code (EN/RU раскладка, без учёта регистра). */
const QUICK_EVENT_HOTKEYS = [
  { id: 1, code: 'KeyG', hint: 'G', labelKey: 'goal' },
  { id: 8, code: 'KeyA', hint: 'A', labelKey: 'mrOG' },
  { id: 3, code: 'KeyY', hint: 'Y', labelKey: 'mrYC' },
  { id: 4, code: 'KeyR', hint: 'R', labelKey: 'mrRC' },
];

const digits2 = (value) => String(value ?? '').replace(/[^0-9]/g, '').slice(0, 2);
const isTypingTarget = (target) => {
  if (!target) return false;
  const tag = String(target.tagName || '').toUpperCase();
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return Boolean(target.closest?.('input, textarea, select, [contenteditable="true"]'));
};
const isPossessionStatistic = (row) => {
  const name = String(row?.name || row?.statistic_name || row?.event || '').toLowerCase();
  return /владен|possession/.test(name);
};
const digitsPercent = (value) => {
  const digits = String(value ?? '').replace(/[^0-9]/g, '').slice(0, 3);
  if (digits === '') return '';
  return String(Math.min(100, Number(digits)));
};
const nextStatisticRow = (row, side, raw) => {
  if (isPossessionStatistic(row)) {
    const value = digitsPercent(raw);
    const other = value === '' ? '' : String(100 - Number(value));
        return {
      ...row,
      home_team_value: side === 'home' ? value : other,
      guest_team_value: side === 'guest' ? value : other,
    };
  }
  return {
    ...row,
    [side === 'home' ? 'home_team_value' : 'guest_team_value']: digits2(raw),
  };
};
const isOn = (value) => value === true || value === 1 || value === '1';
const isPostponed = (item) => isOn(
  item?.extra_data?.rescheduled
  ?? item?.extra_data?.resheduled
  ?? item?.rescheduled
  ?? item?.resheduled
);
const penaltyValue = (value) => (value === '' || value == null ? 0 : Number(value));
const flagsFromMatch = (item) => {
  const settings = item?.settings || {};
  const extra = item?.extra_data || {};
  return {
    halfTime: String(item?.half_time || 30),
    extraTimeAvailable: isOn(settings.extra_time_available ?? item?.extra_time_available),
    extraHalfTime: String(settings.extra_half_time || item?.extra_half_time || 15),
    penaltiesAvailable: isOn(settings.penalty_kicks_available ?? item?.penalty_kicks_available),
    timelineEnabled: !isOn(extra.timeline_disabled ?? item?.timeline_disabled),
  };
};
const scoreFromMatch = (item, fallback) => ({
  home: item?.home_team_scores === '' || item?.home_team_scores == null
    ? (fallback?.home ?? '')
    : String(item.home_team_scores),
  guest: item?.guest_team_scores === '' || item?.guest_team_scores == null
    ? (fallback?.guest ?? '')
    : String(item.guest_team_scores),
});
const normalizeHttpUrl = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
};
const isValidHttpUrl = (value) => {
  try {
    const url = new URL(normalizeHttpUrl(value));
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
    const host = String(url.hostname || '').replace(/^\[|\]$/g, '');
    return Boolean(host) && host.includes('.') && !host.startsWith('.') && !host.endsWith('.');
  } catch (error) {
    return false;
  }
};
const videoTypeLabel = (video, t) => {
  const type = VIDEO_TYPES.find((item) => item.id === Number(video?.type));
  return type ? t(type.labelKey).replace(/^\d+\.\s*/, '') : '';
};
const lineupSignature = (roster) => (roster || [])
  .map((player) => `${player.id}:${player.active ? 1 : 0}:${isMatchKeeper(player) ? MATCH_KEEPER_POSITION : 0}:${player.match_number || ''}:${player.mvp ? 1 : 0}`)
  .join('|');

const MVP_PER_TEAM = 3;

const sameMatchTeam = (left, right) => (
  Number(left.team_id) === Number(right.team_id)
  || (right.isDouble && Number(right.isDouble) === Number(left.team_id))
  || (left.isDouble && Number(left.isDouble) === Number(right.team_id))
  || (left.isDouble && right.isDouble && Number(left.isDouble) === Number(right.isDouble))
);

const videoNamePreview = (value) => {
  const name = String(value || '').trim();
  if (name.length <= 20) return name;
  return `${name.slice(0, 20)}...`;
};
const EventGlyph = ({ id }) => {
  const meta = eventIconMeta(id);
  const [broken, setBroken] = useState(false);
  if (meta && !broken) {
    return (
      <img
        className={`gm-event-icon${meta.invert ? ' is-invert' : ''}`}
        src={meta.src}
        alt=""
        onError={() => setBroken(true)}
      />
    );
  }
  return eventIcon(id);
};

function VideoPreviewThumb({ preview, href }) {
  const [broken, setBroken] = useState(false);
  const showImage = Boolean(preview) && !broken;
  const inner = showImage ? (
    <img src={preview} alt="" onError={() => setBroken(true)} />
  ) : (
    <span className="gm-video-preview-ph" aria-hidden="true"><FiPlay /></span>
  );
  if (!href) {
    return <div className="gm-video-preview">{inner}</div>;
  }
  return (
    <a
      className="gm-video-preview"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(event) => event.stopPropagation()}
    >
      {inner}
    </a>
  );
}

export default function ManageMatchResult(props) {
  const matchId = Number(props.match?.params?.id);
  const dispatch = useDispatch();
  const history = useHistory();
  const { t, lang } = useI18n();
  const { champId, selected: championship, loading: champsLoading } = useChampionships();
  const { canWrite, canWriteMedia } = useAdminAccess('matches');

  const [match, setMatch] = useState(null);
  const [deleteMatchOpen, setDeleteMatchOpen] = useState(false);
  const [deletingMatch, setDeletingMatch] = useState(false);
  const [roster, setRoster] = useState([]);
  const [events, setEvents] = useState([]);
  const [mainEventsOnly, setMainEventsOnly] = useState(false);
  const [score, setScore] = useState({ home: '', guest: '' });
  const [statistics, setStatistics] = useState([]);
  const [comment, setComment] = useState('');
  const [protocolMode, setProtocolMode] = useState(false);
  const [playerMenu, setPlayerMenu] = useState(null);
  const [lineupBaseline, setLineupBaseline] = useState('');
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupStep, setSetupStep] = useState('roster');
  const [setupNotice, setSetupNotice] = useState('');
  const [lineupSetup, setLineupSetup] = useState(emptyLineupSetup);
  const setupTouchedRef = useRef(false);
  const pressTimerRef = useRef(null);
  const [lineupSide, setLineupSide] = useState('home');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [onlineOpen, setOnlineOpen] = useState(false);

  const [countries, setCountries] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [stages, setStages] = useState([]);
  const [stadiums, setStadiums] = useState([]);
  const [pitches, setPitches] = useState([]);
  const [staffCatalog, setStaffCatalog] = useState([]);
  const [tournamentForm, setTournamentForm] = useState({
    champ_id: '',
    country_id: '',
    league_id: '',
    season_id: '',
    stage_id: '',
    net_position: '',
  });
  const [placeForm, setPlaceForm] = useState({ stadium_id: '', pitch_id: '', date: '', time: '' });
  const [staffDraft, setStaffDraft] = useState(null);

  const [techOpen, setTechOpen] = useState(false);
  const [penaltiesOpen, setPenaltiesOpen] = useState(false);
  const [eventEditor, setEventEditor] = useState(null);
  const [staffPickerOpen, setStaffPickerOpen] = useState(false);
  const [staffSearch, setStaffSearch] = useState('');
  const [staffRoleFilter, setStaffRoleFilter] = useState('');
  const [staffPickerError, setStaffPickerError] = useState('');
  const [refereePick, setRefereePick] = useState(null);
  const [videoForm, setVideoForm] = useState(null);
  const [videoUrlError, setVideoUrlError] = useState('');
  const [photoUrlError, setPhotoUrlError] = useState('');
  const [techScore, setTechScore] = useState({ home: '', guest: '' });
  const [penaltyScore, setPenaltyScore] = useState({ home: '', guest: '' });
  const [matchFlags, setMatchFlags] = useState({
    halfTime: '30',
    extraTimeAvailable: false,
    extraHalfTime: '15',
    penaltiesAvailable: false,
    timelineEnabled: true,
  });
  const [savingKey, setSavingKey] = useState('');
  const {
    ref: protocolMenuRef,
    isComponentVisible: protocolMenuOpen,
    setIsComponentVisible: setProtocolMenuOpen,
  } = useComponentVisible(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const [scoreActionsOpen, setScoreActionsOpen] = useState(false);
  const [commentOpen, setCommentOpen] = useState(false);
  const scoreBlurTimer = useRef(null);
  const eventHotkeysRef = useRef({});
  // Таймер онлайн-матча (общий с окном «Онлайн матч»). Без тика: страница перерисовывается только при смене статуса.
  const onlineClock = useOnlineClock(matchId, { tick: false });

  useEffect(() => {
    if (!champId && champsLoading) return;
    loadPage();
  }, [matchId, champId, champsLoading]);

  useEffect(() => {
    const onKeyDown = (event) => {
      const ctx = eventHotkeysRef.current || {};
      if (!ctx.ready) return;

      if (event.key === 'Enter' && ctx.eventEditorOpen) {
        if (String(event.target?.tagName || '').toUpperCase() === 'TEXTAREA') return;
        if (event.target?.closest?.('button, a')) return;
        if (!ctx.canSubmit) return;
        event.preventDefault();
        ctx.saveEvent?.();
        return;
      }

      if (ctx.eventEditorOpen || ctx.modalBlocked || !ctx.canWrite || ctx.protocolMode) return;
      if (event.ctrlKey || event.metaKey || event.altKey || event.repeat) return;
      if (isTypingTarget(event.target)) return;

      const hotkey = QUICK_EVENT_HOTKEYS.find((item) => item.code === event.code);
      if (!hotkey) return;
      event.preventDefault();
      ctx.openQuickEvent?.(hotkey.id);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const showMessage = (text, type = 'success') => setMessage({ text, type });

  const showSetupNotice = (text) => {
    setSetupNotice(text);
    setTimeout(() => setSetupNotice(''), 3000);
  };

  const loadPage = async () => {
    setLoading(true);
    setMessage({ text: '', type: '' });
    try {
      const [matchRes, countriesRes, stadiumsRes, staffRes, lineupRes] = await Promise.all([
        authorizedPost(dispatch, API_URLS.getAdminMatch, { id: matchId, ...champFields(champId) }),
        champId ? authorizedPost(dispatch, API_URLS.getAdminCountries, champFields(champId)).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        authorizedPost(dispatch, API_URLS.getAllAdminStadiums, champFields(champId)).catch(() => ({ data: [] })),
        authorizedPost(dispatch, API_URLS.getAdminStaff, staffRequestBody(champId, { with_games: false })).catch(() => ({ data: [] })),
        authorizedPost(dispatch, API_URLS.getAdminMatchLineup, { match_id: matchId }).catch(() => ({ data: null })),
      ]);
      const nextMatch = extractMatch(matchRes.data);
      const lineups = extractLineups(lineupRes?.data);
      if (lineups) nextMatch.lineups = lineups;
      // Общий таймер с сервера (паузы с другого устройства / пульта табло), когда бэкенд его отдаёт.
      adoptServerClock(matchId, nextMatch);
      setMatch(nextMatch);
      setMatchFlags(flagsFromMatch(nextMatch));
      setScore(scoreFromMatch(nextMatch));
      setStatistics(nextMatch.statistics || []);
      setComment(nextMatch.default_comment || '');
      setPenaltyScore({
        home: nextMatch.penalties == null ? '' : String(nextMatch.penalties.home_team_scores ?? 0),
        guest: nextMatch.penalties == null ? '' : String(nextMatch.penalties.guest_team_scores ?? 0),
      });
      setStaffDraft(normalizeStaffMatch(nextMatch, extractAdminStaff(staffRes.data), championship));
      setStaffCatalog(extractAdminStaff(staffRes.data));
      setCountries(extractList(countriesRes.data));
      const stadiumList = Array.isArray(stadiumsRes.data) ? stadiumsRes.data : extractList(stadiumsRes.data);
      setStadiums(stadiumList);

      const matchDate = String(nextMatch.date || '').slice(0, 10);
      setPlaceForm({
        stadium_id: nextMatch.stadium?.id || '',
        pitch_id: nextMatch.pitch?.id || '',
        date: /^\d{4}-\d{2}-\d{2}$/.test(matchDate) ? matchDate : '',
        time: matchClock(nextMatch.time),
      });
      if (nextMatch.stadium?.id) {
        await loadPitches(nextMatch.stadium.id, nextMatch.pitch?.id);
      }

      const rosterRes = await authorizedPost(dispatch, API_URLS.getRosterEvents, {
        match_id: matchId,
        team_id: [nextMatch.home_team_id, nextMatch.guest_team_id],
        with_doubles: true,
      });
      const nextRoster = extractRoster(rosterRes.data);
      const withMvp = nextRoster.map((player) => ({
        ...player,
        mvp: (nextMatch.best_players || []).some((item) => Number(item.id || item.player_id) === player.id) || player.mvp,
      }));
      setRoster(withMvp);
      setLineupBaseline(lineupSignature(withMvp));

      try {
        const nextEvents = await loadMatchEvents(dispatch, matchId, nextMatch, withMvp);
        setEvents(nextEvents);
      } catch (error) {
        setEvents([]);
      }

      await hydrateTournament(nextMatch, extractList(countriesRes.data));
    } catch (error) {
      setMatch(null);
      showMessage(getApiErrorMessage(error, t('gmLoadError')), 'error');
    } finally {
      setLoading(false);
    }
  };

  const hydrateTournament = async (nextMatch, countryList) => {
    const form = {
      champ_id: nextMatch.champ_id || champId || '',
      country_id: nextMatch.league?.country_id || nextMatch.country_id || '',
      league_id: nextMatch.league_id || '',
      season_id: nextMatch.season_id || '',
      stage_id: nextMatch.stage_id || '',
      net_position: nextMatch.net_position != null ? String(nextMatch.net_position) : '',
    };
    let nextLeagues = [];
    if (form.country_id) {
      nextLeagues = await fetchLeagues(form.country_id);
    } else if (form.league_id && countryList.length) {
      for (const country of countryList) {
        const loaded = await fetchLeagues(country.id);
        if (loaded.some((item) => Number(item.id) === Number(form.league_id))) {
          form.country_id = country.id;
          nextLeagues = loaded;
          break;
        }
      }
    }
    setLeagues(nextLeagues);
    if (form.league_id) {
      const nextSeasons = await fetchSeasons(form.league_id);
      setSeasons(nextSeasons);
      if (form.season_id) setStages(await fetchStages(form.league_id, form.season_id));
    }
    setTournamentForm(form);
  };

  const fetchLeagues = async (countryId) => {
    if (!countryId) return [];
    const response = await authorizedPost(dispatch, API_URLS.getAdminCountry, { id: Number(countryId) });
    return extractList(extractEntity(response.data).leagues);
  };

  const fetchSeasons = async (leagueId) => {
    const response = await authorizedPost(dispatch, API_URLS.getAdminLeague, { id: Number(leagueId) });
    return extractList(extractEntity(response.data).seasons);
  };

  const fetchStages = async (leagueId, seasonId) => {
    const response = await authorizedPost(dispatch, `/leagues/${leagueId}/getLeagueStages`, {
      league_id: Number(leagueId),
      season_id: Number(seasonId),
    });
    return extractList(response.data);
  };

  const loadPitches = async (stadiumId, keepPitchId) => {
    if (!stadiumId) {
      setPitches([]);
      return;
    }
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminStadium, {
        id: Number(stadiumId),
        ...champFields(champId),
      });
      const list = extractList(extractEntity(response.data).pitches);
      setPitches(list);
      if (keepPitchId && list.some((item) => Number(item.id) === Number(keepPitchId))) {
        setPlaceForm((prev) => ({ ...prev, pitch_id: keepPitchId }));
      }
    } catch (error) {
      setPitches([]);
    }
  };

  const teamName = (teamId) => {
    if (Number(teamId) === Number(match?.home_team_id)) return match.home_team?.name;
    if (Number(teamId) === Number(match?.guest_team_id)) return match.guest_team?.name;
    return '';
  };

  const applyMatch = (payload, scoreOverride) => {
    const next = extractMatch(payload);
    if (next?.id) {
      setMatch((prev) => (prev ? {
        ...prev,
        ...next,
        settings: {
          ...(prev.settings || {}),
          ...(next.settings || {}),
        },
      } : next));
      setMatchFlags(flagsFromMatch(next));
      const nextScore = scoreFromMatch(next, scoreOverride);
      if (scoreOverride || nextScore.home !== '' || nextScore.guest !== '') {
        setScore(nextScore);
      }
      setComment(next.default_comment || '');
      setPenaltyScore(
        next.penalties
          ? {
              home: String(next.penalties.home_team_scores ?? 0),
              guest: String(next.penalties.guest_team_scores ?? 0),
            }
          : { home: '', guest: '' }
      );
      if (next.date != null || next.time != null || next.stadium?.id != null) {
        setPlaceForm((prev) => ({
          stadium_id: next.stadium?.id || prev.stadium_id || '',
          pitch_id: next.pitch?.id || prev.pitch_id || '',
          date: next.date && /^\d{4}-\d{2}-\d{2}$/.test(String(next.date).slice(0, 10))
            ? String(next.date).slice(0, 10)
            : prev.date,
          time: next.time != null && next.time !== '' ? matchClock(next.time) : prev.time,
        }));
      }
      const raw = payload?.data && typeof payload.data === 'object' && !Array.isArray(payload.data)
        ? payload.data
        : payload;
      if (Array.isArray(raw?.statistics) || Array.isArray(raw?.match?.statistics)) {
        setStatistics(next.statistics || []);
      }
    } else if (scoreOverride) {
      setScore(scoreOverride);
    }
    return next;
  };

  const refreshMatchCard = async () => {
    const response = await authorizedPost(dispatch, API_URLS.getAdminMatch, { id: matchId, ...champFields(champId) });
    const next = applyMatch(response.data);
    setStatistics(next?.statistics || extractMatchStatistics(response.data) || []);
    return next;
  };

  const reloadEventsAndCard = async () => {
    try {
      const nextEvents = await loadMatchEvents(dispatch, matchId, match, roster);
      setEvents(nextEvents);
    } catch (_error) { /* события уже могли обновиться локально */ }
    try {
      await refreshMatchCard();
    } catch (_error) { /* карточку перечитаем при следующем заходе */ }
  };

  const patchMatchState = async (fields, successKey = 'gmSettingsSaved') => {
    setSavingKey('state');
    try {
      const response = await authorizedPost(dispatch, API_URLS.changeAdminMatchState, {
        match_id: matchId,
        ...fields,
      });
      applyMatch(response.data);
      showMessage(t(successKey));
      return true;
    } catch (error) {
      showMessage(getApiErrorMessage(error, t('gmSaveError')), 'error');
      return false;
    } finally {
      setSavingKey('');
    }
  };

  const saveComment = async () => {
    const trimmed = String(comment || '').trim();
    const ok = await patchMatchState(
      trimmed ? { comment: trimmed } : { delete_comment: true },
      'mrCommentSaved'
    );
    if (ok) setCommentOpen(false);
  };

  const setPlayState = async (condition, extra = {}) => {
    setSavingKey('play');
    const scoreOverride = extra.scoreOverride;
    try {
      const body = {
        match_id: matchId,
        condition,
        team_id: match?.home_team_id,
      };
      if (extra.comment) body.comment = extra.comment;
      if (extra.home_team_scores != null) body.home_team_scores = extra.home_team_scores;
      if (extra.guest_team_scores != null) body.guest_team_scores = extra.guest_team_scores;
      const response = await authorizedPost(dispatch, API_URLS.setAdminMatchPlayState, body);
      const next = applyMatch(response.data, scoreOverride);
      try {
        const nextEvents = await loadMatchEvents(dispatch, matchId, next || match, roster);
        setEvents(nextEvents);
      } catch (_error) {
        // keep current events if the timeline reload fails
      }
      showMessage(t('gmPlayStateSaved'));
      setTechOpen(false);
    } catch (error) {
      showMessage(getApiErrorMessage(error, t('gmSaveError')), 'error');
    } finally {
      setSavingKey('');
    }
  };

  const saveScore = async ({ finish = false, condition, scores } = {}) => {
    if (!match) return;
    setSavingKey('score');
    const source = scores || score;
    const home = source.home === '' || source.home == null ? 0 : Number(source.home);
    const guest = source.guest === '' || source.guest == null ? 0 : Number(source.guest);
    const scoreOverride = { home: String(home), guest: String(guest) };
    const nextCondition = condition || (finish ? finishPlayCondition(match) : null);
    try {
      const body = {
        match_id: matchId,
        home_team_scores: home,
        guest_team_scores: guest,
      };
      if (nextCondition) body.condition = nextCondition;
      if (nextCondition === 'tech_defeat' && comment) body.comment = comment;
      const response = await authorizedPost(dispatch, API_URLS.setAdminMatchScore, body);
      applyMatch(response.data, scoreOverride);
      if (nextCondition) {
        try {
          const nextEvents = await loadMatchEvents(dispatch, matchId, extractMatch(response.data) || match, roster);
          setEvents(nextEvents);
        } catch (_error) {
          // keep current events if the timeline reload fails
        }
      }
      setTechOpen(false);
      setScoreActionsOpen(false);
      showMessage(t(nextCondition === 'end' || nextCondition === 'extra_time_end' ? 'gmScoreFinished' : 'gmScoreSaved'));
    } catch (error) {
      showMessage(getApiErrorMessage(error, t('gmSaveError')), 'error');
    } finally {
      setSavingKey('');
    }
  };

  const saveMatchFlags = async (nextFlags, fields) => {
    setMatchFlags(nextFlags);
    await patchMatchState(fields);
  };

  const deleteMatch = async () => {
    if (!matchId || deletingMatch) return;
    setDeletingMatch(true);
    setMessage({ text: '', type: '' });
    try {
      await authorizedPost(dispatch, API_URLS.deleteMatch, { match_id: matchId });
      setDeleteMatchOpen(false);
      history.push('/admin');
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('gmDeleteMatchError')), type: 'error' });
      setDeleteMatchOpen(false);
    } finally {
      setDeletingMatch(false);
    }
  };

  const saveHalfTime = async (raw) => {
    const next = digits2(raw);
    const value = Number(next) || 30;
    setMatchFlags((prev) => ({ ...prev, halfTime: String(value) }));
    if (Number(match?.half_time) === value) return;
    setSavingKey('half_time');
    try {
      const response = await authorizedPost(dispatch, API_URLS.changeAdminMatch, matchRequestBody(champId, {
        id: matchId,
        half_time: value,
      }));
      applyMatch(response.data);
      showMessage(t('gmSettingsSaved'));
    } catch (error) {
      setMatchFlags((prev) => ({ ...prev, halfTime: String(match?.half_time || 30) }));
      showMessage(getApiErrorMessage(error, t('gmSaveError')), 'error');
    } finally {
      setSavingKey('');
    }
  };

  const saveStatistics = async () => {
    setSavingKey('stats');
    try {
      const rows = statistics
        .filter((row) => Number(row.statistic_id) > 0)
        .map((row) => ({
          statistic_id: Number(row.statistic_id),
          home_team_value: row.home_team_value === '' || row.home_team_value == null ? '' : String(row.home_team_value),
          guest_team_value: row.guest_team_value === '' || row.guest_team_value == null ? '' : String(row.guest_team_value),
        }));
      const response = await authorizedPost(dispatch, API_URLS.setAdminMatchStatistics, {
        match_id: matchId,
        statistics: rows,
      });
      const nextStats = extractMatchStatistics(response.data);
      if (nextStats.length || Array.isArray(response.data) || Array.isArray(response.data?.statistics)) {
        setStatistics(nextStats);
      }
      showMessage(t('gmStatsSaved'));
      setStatsOpen(false);
      resetMobileViewport();
    } catch (error) {
      showMessage(getApiErrorMessage(error, t('gmSaveError')), 'error');
    } finally {
      setSavingKey('');
    }
  };

  const saveTournament = async () => {
    setSavingKey('tournament');
    try {
      const netRaw = tournamentForm.net_position;
      const netPos = Number(netRaw);
      const selectedStage = stages.find((item) => Number(item.id) === Number(tournamentForm.stage_id));
      const stagePlayoff = isPlayoffStage(selectedStage) || isPlayoffStage(match?.stage);
      const payload = {
        id: matchId,
        league_id: Number(tournamentForm.league_id) || undefined,
        season_id: Number(tournamentForm.season_id) || undefined,
        stage_id: Number(tournamentForm.stage_id) || undefined,
      };
      if (stagePlayoff) {
        if (netRaw === '' || netRaw == null) {
          payload.net_position = null;
        } else if (Number.isFinite(netPos) && netPos >= 1) {
          payload.net_position = Math.round(netPos);
        }
      }
      const response = await authorizedPost(dispatch, API_URLS.changeAdminMatch, matchRequestBody(champId, payload));
      applyMatch(response.data);
      showMessage(t('gmTournamentSaved'));
    } catch (error) {
      showMessage(getApiErrorMessage(error, t('gmSaveError')), 'error');
    } finally {
      setSavingKey('');
    }
  };

  const savePlace = async () => {
    const normalizedTime = normalizePartialKickoffTime(placeForm.time);
    // Сервер пишет time только вместе с date (kickoff→UTC). Без даты время молча обнуляется.
    if (normalizedTime && !placeForm.date) {
      showMessage(t('gmTimeNeedsDate'), 'error');
      return;
    }
    if (normalizedTime !== placeForm.time) {
      setPlaceForm((prev) => ({ ...prev, time: normalizedTime }));
    }
    setSavingKey('place');
    try {
      const stadiumObj = stadiums.find((item) => Number(item.id) === Number(placeForm.stadium_id))
        || match?.stadium
        || null;
      const matchTimezone = resolveMatchTimezone(
        stadiumObj,
        match,
        match?.league,
        championship,
        match?.country_name || match?.city_name,
      );
      const kickoff = kickoffFieldsForApi(
        placeForm.date,
        normalizedTime,
        matchTimezone,
      );
      const response = await authorizedPost(dispatch, API_URLS.changeAdminMatch, matchRequestBody(champId, {
        id: matchId,
        stadium_id: placeForm.stadium_id ? Number(placeForm.stadium_id) : null,
        pitch_id: placeForm.pitch_id ? Number(placeForm.pitch_id) : null,
        date: kickoff.date,
        time: kickoff.time,
        timezone: matchTimezone,
      }));
      applyMatch(response.data);
      setPlaceForm((prev) => ({ ...prev, date: placeForm.date || '', time: normalizedTime || placeForm.time || '' }));
      showMessage(t('gmPlaceSaved'));
    } catch (error) {
      showMessage(getApiErrorMessage(error, t('gmSaveError')), 'error');
    } finally {
      setSavingKey('');
    }
  };

  const saveStaff = async (nextMatch) => {
    setSavingKey('staff');
    try {
      const payload = matchToSavePayload(nextMatch || staffDraft);
      const response = await authorizedPost(dispatch, API_URLS.setAdminStaffMatch, payload);
      const next = applyMatch(response.data);
      setStaffDraft(normalizeStaffMatch(next || response.data, staffCatalog, championship));
      showMessage(t('gmStaffSaved'));
    } catch (error) {
      showMessage(getApiErrorMessage(error, t('gmSaveError')), 'error');
    } finally {
      setSavingKey('');
    }
  };

  const saveEvent = async () => {
    if (!eventEditor) return;
    if (String(eventEditor.minute ?? '').trim() === '') {
      showMessage(t('gmMinuteRequired'), 'error');
      return;
    }
    const isEdit = Boolean(eventEditor.id);
    const resolvedEventId = resolveYellowCardEventId(
      eventEditor.event_id,
      events,
      eventEditor.player_id,
      { isEdit },
    );
    const typeInfo = eventTypeById(resolvedEventId);
    const isSub = Number(resolvedEventId) === 15;
    if (typeInfo?.needsPlayer && !eventEditor.player_id) {
      showMessage(t('gmNeedEventPlayer'), 'error');
      return;
    }
    if (isSub && !eventEditor.additional_player_id) {
      showMessage(t('gmNeedEventPlayerIn'), 'error');
      return;
    }
    const clock = toEventTime(
      eventEditor.minute,
      eventEditor.second,
      match.half_time,
      match?.settings?.extra_half_time || match?.extra_half_time,
    );
    const comment = eventCommentText(eventEditor);
    // Тайм по этапу матча (с онлайн-таймера / прежний у редактируемого события), если время не правили руками.
    const timeNumber = hintedTimeNumber(eventEditor.time_hint, eventEditor.minute, eventEditor.second)
      || clock.time_number;
    const body = {
      match_id: matchId,
      event_id: Number(resolvedEventId),
      player_id: eventEditor.player_id ? Number(eventEditor.player_id) : null,
      team_id: eventEditor.team_id ? Number(eventEditor.team_id) : null,
      additional_player_id: eventEditor.additional_player_id ? Number(eventEditor.additional_player_id) : null,
      time: clock.time,
      time_number: timeNumber,
      comment,
      note: comment,
    };
    setSavingKey('event');
    try {
      if (isEdit) {
        await authorizedPost(dispatch, API_URLS.changeMatchEvent, {
          ...body,
          event_id: eventEditor.id,
          event_type: Number(resolvedEventId),
          previous_event_type: eventEditor.previous_event_id,
          previous_team_id: eventEditor.previous_team_id,
          additional_event_id: eventEditor.additional_event_id || null,
        });
      } else {
        await authorizedPost(dispatch, API_URLS.addAdminMatchEvent, body);
      }
      await reloadEventsAndCard();
      setEventEditor(null);
      if (!isEdit && resolvedEventId === SECOND_YELLOW_CARD_EVENT_ID
        && Number(eventEditor.event_id) === YELLOW_CARD_EVENT_ID) {
        const player = roster.find((item) => Number(item.id) === Number(eventEditor.player_id));
        const name = player ? playerDisplayName(player) : '';
        showMessage(name ? t('olm2YC', { name }) : t('gmEventSaved'));
      } else {
        showMessage(t('gmEventSaved'));
      }
    } catch (error) {
      showMessage(getApiErrorMessage(error, t('gmSaveError')), 'error');
    } finally {
      setSavingKey('');
    }
  };

  const deleteEvent = async () => {
    if (!eventEditor?.id) {
      setEventEditor(null);
      return;
    }
    setSavingKey('event');
    const isGoal = Number(eventEditor.event_id) === 1;
    const clock = toEventTime(
      eventEditor.minute,
      eventEditor.second,
      match.half_time,
      match?.settings?.extra_half_time || match?.extra_half_time,
    );
    try {
      await authorizedPost(dispatch, API_URLS.removeMatchEvent, {
        match_id: matchId,
        event_id: eventEditor.id,
        event_type: eventEditor.event_id,
        team_id: eventEditor.team_id,
        additional_event_id: isGoal ? (eventEditor.additional_event_id || null) : null,
        additional_player_id: isGoal ? (eventEditor.additional_player_id || null) : null,
        time: eventEditor.time || clock.time,
        time_number: eventEditor.time_number || clock.time_number,
      });
      await reloadEventsAndCard();
      setEventEditor(null);
      showMessage(t('gmEventDeleted'));
    } catch (error) {
      showMessage(getApiErrorMessage(error, t('gmSaveError')), 'error');
    } finally {
      setSavingKey('');
    }
  };

  const savePenalties = async () => {
    setSavingKey('penalties');
    try {
      const response = await authorizedPost(dispatch, API_URLS.setAdminMatchPenalties, {
        match_id: matchId,
        home_team_scores: penaltyValue(penaltyScore.home),
        guest_team_scores: penaltyValue(penaltyScore.guest),
      });
      applyMatch(response.data);
      setPenaltiesOpen(false);
      showMessage(t('gmPenaltiesSaved'));
    } catch (error) {
      showMessage(getApiErrorMessage(error, t('gmSaveError')), 'error');
    } finally {
      setSavingKey('');
    }
  };

  const deletePenalties = async () => {
    setSavingKey('penalties');
    try {
      const response = await authorizedPost(dispatch, API_URLS.deleteAdminMatchPenalties, { match_id: matchId });
      applyMatch(response.data);
      setPenaltiesOpen(false);
      showMessage(t('gmPenaltiesDeleted'));
    } catch (error) {
      showMessage(getApiErrorMessage(error, t('gmSaveError')), 'error');
    } finally {
      setSavingKey('');
    }
  };

  const closeVideoForm = () => {
    setVideoForm(null);
    setVideoUrlError('');
  };

  const saveVideo = async () => {
    if (!videoForm) return;
    const video = normalizeHttpUrl(videoForm.video);
    if (!isValidHttpUrl(video)) {
      setVideoUrlError(t('gmInvalidVideoUrl'));
      return;
    }
    setVideoUrlError('');
    setSavingKey('video');
    try {
      const payload = {
        match_id: matchId,
        type: Number(videoForm.type) || 1,
        video,
        name: videoForm.name || '',
      };
      const response = videoForm.id
        ? await authorizedPost(dispatch, API_URLS.changeAdminMatchVideo, { ...payload, id: videoForm.id })
        : await authorizedPost(dispatch, API_URLS.addAdminMatchVideo, payload);
      const list = Array.isArray(response.data) ? response.data : extractList(response.data);
      setMatch((prev) => ({ ...prev, videos: list.length ? list : prev.videos }));
      closeVideoForm();
      showMessage(t('gmVideoSaved'));
    } catch (error) {
      showMessage(getApiErrorMessage(error, t('gmSaveError')), 'error');
    } finally {
      setSavingKey('');
    }
  };

  const openVideoForm = (video) => {
    setVideoUrlError('');
    setVideoForm(video ? {
      id: video.id,
      type: video.type || 1,
      video: video.video || '',
      name: video.name || '',
    } : { type: 1, video: '', name: '' });
  };

  const isAddingVideo = Boolean(videoForm) && videoForm.id == null;
  const isEditingVideo = (video) => Boolean(videoForm) && videoForm.id != null && Number(videoForm.id) === Number(video.id);

  const renderVideoForm = () => (
    <div className="gm-video-form">
      <select
        className="st-select"
        value={videoForm.type}
        onChange={(e) => setVideoForm((prev) => ({ ...prev, type: e.target.value }))}
      >
        {VIDEO_TYPES.map((item) => <option key={item.id} value={item.id}>{t(item.labelKey)}</option>)}
      </select>
      <input
        className="st-input"
        value={videoForm.name || ''}
        onChange={(e) => setVideoForm((prev) => ({ ...prev, name: e.target.value }))}
        placeholder={t('gmVideoName')}
      />
      <input
        className={`st-input${videoUrlError ? ' error' : ''}`}
        value={videoForm.video || ''}
        onChange={(e) => {
          setVideoUrlError('');
          setVideoForm((prev) => ({ ...prev, video: e.target.value }));
        }}
        placeholder={t('gmVideoUrl')}
      />
      {videoUrlError ? <div className="st-message error">{videoUrlError}</div> : null}
      <div className="gm-card-actions">
        <button type="button" className="st-btn st-btn-ghost" onClick={closeVideoForm}>{t('cancel')}</button>
        <button type="button" className="st-btn st-btn-primary" disabled={savingKey === 'video'} onClick={saveVideo}>{t('save')}</button>
      </div>
    </div>
  );

  const deleteVideo = async (video) => {
    if (videoForm && Number(videoForm.id) === Number(video.id)) closeVideoForm();
    try {
      const response = await authorizedPost(dispatch, API_URLS.deleteAdminMatchVideo, { match_id: matchId, id: video.id });
      const list = Array.isArray(response.data) ? response.data : extractList(response.data);
      setMatch((prev) => ({ ...prev, videos: list }));
      showMessage(t('gmVideoDeleted'));
    } catch (error) {
      showMessage(getApiErrorMessage(error, t('gmSaveError')), 'error');
    }
  };

  const applyPhotoSet = (payload) => {
    const next = extractPhotoSetPayload(payload);
    setMatch((prev) => (prev ? {
      ...prev,
      photoSetUrl: next.photoSetUrl,
      photo_set: next.photo_set,
    } : prev));
  };

  const savePhotoSet = async () => {
    const photoSetUrl = normalizeHttpUrl(match?.photoSetUrl);
    if (!isValidHttpUrl(photoSetUrl)) {
      setPhotoUrlError(t('gmInvalidPhotoUrl'));
      return;
    }
    setPhotoUrlError('');
    setSavingKey('photos');
    try {
      const response = await authorizedPost(dispatch, API_URLS.setAdminMatchPhotoSet, {
        match_id: matchId,
        photoSetUrl,
      });
      applyPhotoSet(response.data);
      showMessage(t('stSaved'));
    } catch (error) {
      showMessage(getApiErrorMessage(error, t('gmSaveError')), 'error');
    } finally {
      setSavingKey('');
    }
  };

  const deletePhotoSet = async () => {
    setSavingKey('photos');
    try {
      const response = await authorizedPost(dispatch, API_URLS.deleteAdminMatchPhotoSet, { match_id: matchId });
      applyPhotoSet(response.data);
    } catch (error) {
      showMessage(getApiErrorMessage(error, t('gmSaveError')), 'error');
    } finally {
      setSavingKey('');
    }
  };

  const toggleMvp = (player) => {
    if (!player.mvp) {
      const teamCount = roster.filter((item) => item.mvp && sameMatchTeam(item, player)).length;
      if (teamCount >= MVP_PER_TEAM) {
        showMessage(t('gmMaxMvp', { count: MVP_PER_TEAM }), 'error');
        return;
      }
    }
    setRoster((prev) => prev.map((item) => (
      Number(item.id) === Number(player.id) ? { ...item, mvp: !player.mvp } : item
    )));
  };

  const toggleKeeper = (player) => {
    const makeKeeper = !isMatchKeeper(player);
    dropCaptainMarkForTeam(player.team_id);
    setRoster((prev) => prev.map((item) => {
      if (Number(item.id) === Number(player.id)) {
        return { ...item, match_position: makeKeeper ? MATCH_KEEPER_POSITION : null };
      }
      if (makeKeeper && Number(item.team_id) === Number(player.team_id) && isMatchKeeper(item)) {
        return { ...item, match_position: null };
      }
      return item;
    }));
  };

  const isInteractiveTarget = (target) => Boolean(target?.closest?.('input, button, a, label, select, textarea'));

  const openPlayerMenu = (event, player) => {
    if (!canWrite || isInteractiveTarget(event.target)) return;
    event.preventDefault();
    const point = event.touches?.[0] || event.changedTouches?.[0] || event;
    setPlayerMenu({ player, x: point.clientX, y: point.clientY });
  };

  const cancelPlayerPress = () => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
  };

  const startPlayerPress = (event, player) => {
    if (!canWrite || (event.button != null && event.button !== 0) || isInteractiveTarget(event.target)) return;
    const point = event.touches?.[0] || event;
    const x = point.clientX;
    const y = point.clientY;
    cancelPlayerPress();
    pressTimerRef.current = setTimeout(() => {
      pressTimerRef.current = null;
      setPlayerMenu({ player, x, y });
    }, 450);
  };

  useEffect(() => cancelPlayerPress, []);

  useEffect(() => {
    if (!playerMenu) return undefined;
    const onKeyDown = (event) => { if (event.key === 'Escape') setPlayerMenu(null); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [playerMenu]);

  const applyRosterEvents = (data) => {
    const next = extractRosterEvents(data, match, roster);
    if (next.roster.length && next.recognized) {
      const byId = new Map(next.roster.map((player) => [player.id, player]));
      const merged = roster.map((player) => {
        const updated = byId.get(player.id);
        return updated ? { ...player, ...updated } : { ...player, active: false, mvp: null };
      });
      setRoster(merged);
      setLineupBaseline(lineupSignature(merged));
    }
    setEvents(next.events);
  };

  // Ответ setAdminMatchApplication — application + best_players, события не трогаем.
  const playerOnTeam = (player, teamId) => (
    Number(player.team_id) === Number(teamId) || Number(player.isDouble) === Number(teamId)
  );

  const applyMatchApplication = (data, teamId = null) => {
    const next = extractRosterEvents(data, match, roster);
    if (!next.roster.length || !next.recognized) return;
    const byId = new Map(next.roster.map((player) => [player.id, player]));
    const merged = roster.map((player) => {
      const onScopedTeam = teamId == null || playerOnTeam(player, teamId);
      const updated = byId.get(player.id);
      if (updated) {
        return { ...player, ...updated, from_captain: false };
      }
      if (!onScopedTeam) return player;
      return { ...player, active: false, from_captain: false, mvp: null };
    });
    setRoster(merged);
    setLineupBaseline(lineupSignature(merged));
  };

  const markCaptainLineupApproved = (lineups, teamId = null) => {
    setRoster((prev) => prev.map((player) => {
      if (!player.from_captain) return player;
      if (teamId != null && !playerOnTeam(player, teamId)) return player;
      return { ...player, from_captain: false };
    }));
    setMatch((prev) => {
      if (!prev) return prev;
      const home = teamId == null || Number(teamId) === Number(prev.home_team_id);
      const guest = teamId == null || Number(teamId) === Number(prev.guest_team_id);
      return {
        ...prev,
        lineups: lineups || prev.lineups,
        has_home_lineup: home ? true : prev.has_home_lineup,
        has_guest_lineup: guest ? true : prev.has_guest_lineup,
        has_home_cap_lineup: home ? false : prev.has_home_cap_lineup,
        has_guest_cap_lineup: guest ? false : prev.has_guest_cap_lineup,
      };
    });
  };

  const dropCaptainMarkForTeam = (teamId) => {
    if (!teamId) return;
    setRoster((prev) => {
      if (!prev.some((player) => player.from_captain && playerOnTeam(player, teamId))) return prev;
      return prev.map((player) => (
        player.from_captain && playerOnTeam(player, teamId) ? { ...player, from_captain: false } : player
      ));
    });
    setMatch((prev) => {
      if (!prev) return prev;
      const home = Number(teamId) === Number(prev.home_team_id);
      const guest = Number(teamId) === Number(prev.guest_team_id);
      if (!home && !guest) return prev;
      const withoutCaptain = (row) => (row && typeof row === 'object' ? { ...row, from_captain: false } : row);
      let lineups = prev.lineups;
      if (Array.isArray(lineups)) {
        lineups = lineups.map((row) => (Number(row?.team_id) === Number(teamId) ? withoutCaptain(row) : row));
      } else if (lineups && typeof lineups === 'object') {
        lineups = { ...lineups };
        [teamId, String(teamId)].forEach((key) => {
          if (lineups[key]) lineups[key] = withoutCaptain(lineups[key]);
        });
      }
      return {
        ...prev,
        lineups,
        has_home_cap_lineup: home ? false : prev.has_home_cap_lineup,
        has_guest_cap_lineup: guest ? false : prev.has_guest_cap_lineup,
      };
    });
  };

  const toggleRosterPlayer = (player, teamId) => {
    dropCaptainMarkForTeam(teamId || player.team_id);
    setRoster((prev) => prev.map((item) => (
      Number(item.id) === Number(player.id) ? { ...item, active: !item.active } : item
    )));
  };

  const submitProtocol = async () => {
    if (!match) return;
    const payload = buildProtocolRosterEvents({ match, roster });
    if (payload.errorPlayers.length) {
      const first = payload.errorPlayers[0];
      showMessage(t('mrPlayerNotListed', { name: playerDisplayName(first), team: first.team_name || '' }), 'error');
      return;
    }
    setSavingKey('protocol');
    try {
      const { errorPlayers, ...body } = payload;
      const response = await authorizedPost(dispatch, API_URLS.setAdminMatchRosterEvents, body);
      applyRosterEvents(response.data);
      try {
        await refreshMatchCard();
      } catch (_error) {
        // Состав и события уже записаны, карточку перечитаем при следующем заходе.
      }
      showMessage(t('mrResultsSaved'));
    } catch (error) {
      showMessage(getApiErrorMessage(error, t('gmSaveError')), 'error');
    } finally {
      setSavingKey('');
    }
  };

  // Расставленного игрока сервер держит в заявке, поэтому он остаётся выбранным; неизвестных убираем.
  const withServerSetup = (base, fromServer) => {
    if (!fromServer) return base;
    const known = new Set(roster.map((player) => player.id));
    const selectedPlayers = { ...base.selectedPlayers };
    const keepKnown = (lineup) => Object.fromEntries(
      Object.entries(lineup || {}).filter(([, playerId]) => {
        if (!known.has(Number(playerId))) return false;
        selectedPlayers[playerId] = true;
        return true;
      })
    );
    const homeLineup = keepKnown(fromServer.homeLineup);
    const guestLineup = keepKnown(fromServer.guestLineup);
    return { ...base, ...fromServer, homeLineup, guestLineup, selectedPlayers };
  };

  const openLineupSetup = async () => {
    // Схемы берём с сервера, из сессии — только то, чего сервер не хранит (цвета формы).
    const saved = loadLineupSetup(matchId) || {};
    const defaults = emptyLineupSetup();
    const base = {
      ...defaults,
      ...setupFromRoster(roster),
      homeColor: saved.homeColor || defaults.homeColor,
      guestColor: saved.guestColor || defaults.guestColor,
    };
    const cached = withServerSetup(base, setupFromServer(match.lineups, match.home_team_id, match.guest_team_id));
    setupTouchedRef.current = false;
    setLineupSetup(cached);
    setSetupStep('roster');
    setSetupNotice('');
    setSetupOpen(true);
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminMatchLineup, { match_id: match.id });
      const lineups = extractLineups(response.data);
      if (!lineups) return;
      setMatch((prev) => (prev ? { ...prev, lineups } : prev));
      // Если админ успел что-то поменять, пока шёл запрос, его правки важнее.
      if (setupTouchedRef.current) return;
      const fresh = withServerSetup(base, setupFromServer(lineups, match.home_team_id, match.guest_team_id));
      setLineupSetup(fresh);
      setSetupStep('roster');
    } catch (error) {
      setSetupNotice(getApiErrorMessage(error, t('gmSaveError')));
    }
  };

  const submitLineupSetup = async () => {
    const setup = lineupSetup;
    const keeperIds = new Set([setup.homeLineup[0], setup.guestLineup[0]].filter(Boolean).map(Number));
    const nextRoster = roster.map((player) => ({
      ...player,
      active: Boolean(setup.selectedPlayers[player.id]),
      match_position: keeperIds.has(Number(player.id))
        ? MATCH_KEEPER_POSITION
        : (isMatchKeeper(player) ? null : player.match_position),
    }));
    setRoster(nextRoster);
    saveLineupSetup({ matchId: Number(matchId), phase: 'lineup', ...setup });
    // Окно онлайна берёт расстановку отсюда — правка на странице матча должна туда попасть.
    saveOnlineMatch(matchId, {
      setup: {
        selectedPlayers: setup.selectedPlayers,
        matchFormat: setup.matchFormat,
        homeFormation: setup.homeFormation,
        guestFormation: setup.guestFormation,
        homeLineup: setup.homeLineup,
        guestLineup: setup.guestLineup,
        homeColor: setup.homeColor,
        guestColor: setup.guestColor,
      },
    });
    setSetupOpen(false);
    setSavingKey('lineup');
    try {
      // Сначала только состав — без событий и завершения матча.
      const response = await authorizedPost(
        dispatch,
        API_URLS.setAdminMatchApplication,
        buildMatchApplicationPayload({ match, roster: nextRoster })
      );
      applyMatchApplication(response.data);
      const lineupResponse = await authorizedPost(dispatch, API_URLS.setAdminMatchLineup, {
        match_id: match.id,
        match_format: setup.matchFormat,
        lineups: [
          {
            team_id: match.home_team_id,
            formation: setup.homeFormation,
            assignments: lineupToAssignments(setup.homeFormation, setup.homeLineup),
          },
          {
            team_id: match.guest_team_id,
            formation: setup.guestFormation,
            assignments: lineupToAssignments(setup.guestFormation, setup.guestLineup),
          },
        ],
      });
      const lineups = extractLineups(lineupResponse.data);
      markCaptainLineupApproved(lineups);
      showMessage(t('gmLineupSetupSaved'));
    } catch (error) {
      showMessage(getApiErrorMessage(error, t('gmSaveError')), 'error');
    } finally {
      setSavingKey('');
    }
  };

  const saveLineup = async () => {
    if (!match) return;
    setSavingKey('lineup');
    try {
      const approveOnly = captainLineupPending && !lineupDirty;
      let teamId = null;
      if (approveOnly) {
        if (homeCaptainPending && !guestCaptainPending) teamId = match.home_team_id;
        else if (guestCaptainPending && !homeCaptainPending) teamId = match.guest_team_id;
      }
      const body = approveOnly
        ? buildApproveCaptainPayload({ match, teamId })
        : buildMatchApplicationPayload({ match, roster });
      const response = await authorizedPost(dispatch, API_URLS.setAdminMatchApplication, body);
      applyMatchApplication(response.data, approveOnly ? teamId : null);
      markCaptainLineupApproved(null, approveOnly ? teamId : null);
      showMessage(t('gmLineupSaved'));
    } catch (error) {
      showMessage(getApiErrorMessage(error, t('gmSaveError')), 'error');
    } finally {
      setSavingKey('');
    }
  };

  const lineupDirty = useMemo(() => lineupSignature(roster) !== lineupBaseline, [roster, lineupBaseline]);
  const homeCaptainPending = useMemo(() => (
    teamHasUnapprovedCaptainLineup({ match, roster, teamId: match?.home_team_id })
  ), [match, roster]);
  const guestCaptainPending = useMemo(() => (
    teamHasUnapprovedCaptainLineup({ match, roster, teamId: match?.guest_team_id })
  ), [match, roster]);
  const visibleRoster = useMemo(() => {
    const teamId = lineupSide === 'home' ? match?.home_team_id : match?.guest_team_id;
    return roster.filter((player) => Number(player.team_id) === Number(teamId) || Number(player.isDouble) === Number(teamId));
  }, [roster, lineupSide, match]);
  // Схема для превью на карточке матча: формации с сервера, цвета формы — из сессии.
  const serverSetup = useMemo(() => {
    const setup = setupFromServer(match?.lineups, match?.home_team_id, match?.guest_team_id);
    if (!setup || (!setup.homeFormation && !setup.guestFormation)) return null;
    const saved = loadLineupSetup(matchId) || {};
    const defaults = emptyLineupSetup();
    return {
      ...setup,
      homeColor: saved.homeColor || defaults.homeColor,
      guestColor: saved.guestColor || defaults.guestColor,
    };
  }, [match, matchId]);
  const timelineEvents = useMemo(() => {
    const list = withMatchTimelineEvents(events, match);
    if (!mainEventsOnly) return list;
    return list.filter((event) => (
      isMatchServiceEvent(event)
      || MAIN_TIMELINE_EVENT_IDS.includes(Number(event.event_id))
    ));
  }, [events, match, mainEventsOnly]);
  const staffRoleOptions = useMemo(() => {
    const order = ROLE_SLOT_GROUPS.map((item) => item.role);
    const ids = [...new Set(staffCatalog.map((person) => Number(person.role)).filter(Boolean))];
    return ids
      .sort((left, right) => {
        const leftIndex = order.indexOf(left);
        const rightIndex = order.indexOf(right);
        return (leftIndex < 0 ? 99 : leftIndex) - (rightIndex < 0 ? 99 : rightIndex);
      })
      .map((id) => ({ id, label: roleLabel(t, id) }));
  }, [staffCatalog, t]);
  const filteredStaffCatalog = useMemo(() => {
    const query = staffSearch.trim().toLowerCase();
    const roleId = Number(staffRoleFilter) || 0;
    return staffCatalog.filter((person) => {
      if (roleId && Number(person.role) !== roleId) return false;
      if (!query) return true;
      const haystack = [
        person.name,
        person.family,
        person.father_name,
        person.login,
        person.nickname,
        staffDisplayName(person),
        person.position,
        roleLabel(t, person.role),
      ].join(' ').toLowerCase();
      return haystack.includes(query);
    });
  }, [staffCatalog, staffSearch, staffRoleFilter, t]);

  const captainLineupPending = Boolean(
    homeCaptainPending
    || guestCaptainPending
    || matchHasUnapprovedCaptainLineup({ match, roster })
  );
  const allVisibleActive = visibleRoster.length > 0 && visibleRoster.every((player) => player.active);

  const toggleAllActive = () => {
    const teamId = lineupSide === 'home' ? match?.home_team_id : match?.guest_team_id;
    const ids = new Set(visibleRoster.map((player) => player.id));
    dropCaptainMarkForTeam(teamId);
    setRoster((prev) => prev.map((player) => (
      ids.has(player.id) ? { ...player, active: !allVisibleActive } : player
    )));
  };

  const openStaffPicker = () => {
    setStaffSearch('');
    setStaffRoleFilter('');
    setStaffPickerError('');
    setRefereePick(null);
    setStaffPickerOpen(true);
  };

  const closeStaffPicker = () => {
    setStaffPickerOpen(false);
    setStaffSearch('');
    setStaffRoleFilter('');
    setStaffPickerError('');
    setRefereePick(null);
  };

  const staffRoleText = (person) => {
    const slotKey = refereeSlotLabelKey(person?.slot);
    if (slotKey) return t(slotKey);
    return person?.position || roleLabel(t, person?.role);
  };

  const staffAssignError = (reason, person) => {
    const name = staffDisplayName(person);
    const role = roleLabel(t, person.role);
    if (reason === 'already') return t('msAlreadyAssigned', { name });
    if (reason === 'full') return t('msSlotsFull', { role });
    if (reason === 'no-slot') return t('msInspectorHint');
    return t('msCannotAssign');
  };

  const commitStaffPerson = (person, slot) => {
    const result = assignPersonToMatch(staffDraft, person, slot);
    if (!result.ok) {
      setStaffPickerError(staffAssignError(result.reason, person));
      return;
    }
    setStaffDraft(result.match);
    closeStaffPicker();
    saveStaff(result.match);
  };

  const pickStaffPerson = (person) => {
    setStaffPickerError('');
    if (isRefereeRole(person.role)) {
      const target = assignmentTarget(staffDraft, person);
      if (!target.ok) {
        setStaffPickerError(staffAssignError(target.reason, person));
        return;
      }
      setRefereePick({ person, slots: target.slots });
      return;
    }
    commitStaffPerson(person);
  };
  const playerEvents = (playerId) => events.flatMap((event) => {
    if (Number(event.player_id) === Number(playerId)) return [event];
    if (Number(event.additional_player_id) === Number(playerId) && Number(event.event_id) === 1) {
      return [{ ...event, event_id: 2, id: `assist-${event.id || event.server_id}` }];
    }
    return [];
  });
  const editableEventTypes = MATCH_EVENT_TYPES.filter((item) => !MATCH_SERVICE_EVENT_IDS.includes(item.id));
  // Локальный онлайн-таймер (с паузами) важнее; иначе — оценка по start_in / second_time_start с сервера.
  const onlineLive = isClockLive(onlineClock.clock) && clockMatchesServer(onlineClock.clock, match);
  const onlineEventTime = (seconds = clockSeconds(onlineClock.clock), clock = onlineClock.clock) => {
    const { minute, second } = splitSeconds(seconds);
    return {
      minute: String(minute),
      second: String(second).padStart(2, '0'),
      time_hint: { minute, second, time_number: timeNumberForStatus(clock?.status) },
    };
  };
  const liveEventTime = () => {
    if (onlineLive) return onlineEventTime();
    const estimate = estimateLiveEventClock(match);
    if (!estimate) return {};
    // Тайм — по этапу матча на сервере, а не по минуте.
    const time_number = timeNumberForStatus(statusForPhase(matchPlayPhase(match), true));
    return {
      ...estimate,
      time_hint: { minute: Number(estimate.minute), second: Number(estimate.second) || 0, time_number },
    };
  };
  const newEventForm = (patch = {}) => ({
    ...emptyEventForm,
    ...liveEventTime(),
    ...patch,
  });
  const openQuickEvent = (eventId) => {
    setEventEditor(newEventForm({
      event_id: Number(eventId),
      team_id: match?.home_team_id || '',
    }));
  };
  const eventModalBusy = Boolean(
    techOpen || penaltiesOpen || staffPickerOpen || onlineOpen || setupOpen
    || playerMenu || videoForm || commentOpen || scoreActionsOpen,
  );
  const canSubmitEvent = Boolean(
    eventEditor
    && savingKey !== 'event'
    && String(eventEditor.minute ?? '').trim() !== ''
    && !(eventTypeById(eventEditor.event_id)?.needsPlayer && !eventEditor.player_id)
    && !(Number(eventEditor.event_id) === 15 && !eventEditor.additional_player_id),
  );
  eventHotkeysRef.current = {
    ready: !loading && Boolean(match),
    canWrite,
    protocolMode,
    eventEditorOpen: Boolean(eventEditor),
    modalBlocked: eventModalBusy,
    canSubmit: canSubmitEvent,
    openQuickEvent,
    saveEvent,
  };

  if (loading) {
      return (
      <div className="layoutWrapper st-layout">
        <div className="matchCenterWrapper st-page">
          <div className="st-loading">
            <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
            <p>{t('mrLoading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!match) {
    return (
      <div className="layoutWrapper st-layout">
        <div className="matchCenterWrapper st-page">
          <div className="matchCenterHeader">
            <StadiumCrumbs items={[{ label: t('navMatches'), to: '/admin' }, { label: t('gmLoadError') }]} />
              </div>
          {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : (
            <div className="st-empty"><h3>{t('gmLoadError')}</h3></div>
          )}
              </div>
      </div>
    );
  }

  const statusKey = matchStatusKey(match);
  const playPhase = matchPlayPhase(match);
  const playValue = playPhaseSelectValue(playPhase);
  const playOptions = MATCH_PLAY_OPTIONS.filter((item) => !item.extraOnly || matchFlags.extraTimeAvailable || playValue === item.value);
  const eventType = eventEditor ? eventTypeById(eventEditor.event_id) : null;
  const eventTeamPlayers = (teamId, keepId) => {
    const onTeam = roster.filter((player) => playerOnTeam(player, teamId));
    const listed = onTeam.filter((player) => player.active);
    const admin = listed.filter((player) => !player.from_captain);
    const source = admin.length ? admin : listed.filter((player) => player.from_captain);
    if (keepId && !source.some((player) => Number(player.id) === Number(keepId))) {
      const kept = onTeam.find((player) => Number(player.id) === Number(keepId));
      if (kept) return source.concat(kept);
    }
    return source;
  };
  const homeName = teamCardName(match.home_team);
  const guestName = teamCardName(match.guest_team);
  const eventPlayer = (event) => roster.find((item) => Number(item.id) === Number(event.player_id));
  const eventAssistPlayer = (event) => roster.find((item) => Number(item.id) === Number(event.additional_player_id));
  const eventTeam = (event) => {
    if (Number(event.team_id) === Number(match.home_team_id)) return match.home_team;
    if (Number(event.team_id) === Number(match.guest_team_id)) return match.guest_team;
    return null;
  };
  const eventPersonParts = (event, side) => {
    const fromRoster = side === 'assist' ? eventAssistPlayer(event) : eventPlayer(event);
    if (fromRoster) return playerEventParts(fromRoster);
    const fallback = side === 'assist' ? event.second_name : event.player_name;
    const name = fallback && fallback !== '#' ? fallback : '';
    return { name, number: side === 'assist' ? '' : (event.player_number || '') };
  };
  const renderEventPerson = (event, side) => {
    const { name, number } = eventPersonParts(event, side);
    if (!name && !number) return null;
    return (
      <>
        {name}
        {number ? <span className="gm-event-num">{number}</span> : null}
      </>
    );
  };
          return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs items={[
              { label: t('navMatches'), to: '/admin' },
              { label: `${homeName} — ${guestName}` },
            ]} />
            <div className="gm-card-actions">
              <button type="button" className="st-btn st-btn-secondary" onClick={() => setOnlineOpen(true)}>
                {t('mrOnlineMatch')}
              </button>
              <IfCanWrite entity="matches">
                <button
                  type="button"
                  className="st-btn st-btn-danger"
                  disabled={deletingMatch}
                  onClick={() => setDeleteMatchOpen(true)}
                >
                  <FiTrash2 /> {t('gmDeleteMatch')}
                </button>
              </IfCanWrite>
      </div>
          </div>
        </div>

        <div className={`st-message gm-message ${message.text ? message.type : 'is-idle'}`}>
          {message.text || t('gmMessageIdle')}
        </div>

        <div className="st-scroll">
          <div className={`st-content gm-match-page${protocolMode ? ' is-protocol' : ''}`}>
            <div className="gm-match-side">
            <div className="st-block">
              <div className="gm-card-header">
                <h2 className="st-block-header">{t('gmResult')}</h2>
                <span className={`gm-status ${statusKey === 'matchFinished' || statusKey === 'technicalDefeat' ? 'is-done' : ''}`}>{t(statusKey)}</span>
          </div>
              <div className="gm-scoreboard">
                <div className="gm-scoreboard-team is-home">
                  <span className="gm-scoreboard-name">{homeName}</span>
                  <img src={getTeamImageUrl(match.home_team?.image) || defaultTeamLogo} alt="" />
          </div>
                <div className="gm-score-inputs">
                  <input
                    className="st-input"
                    maxLength={2}
                    inputMode="numeric"
                    value={score.home}
                    onFocus={() => {
                      if (scoreBlurTimer.current) window.clearTimeout(scoreBlurTimer.current);
                      setScoreActionsOpen(true);
                    }}
                    onBlur={() => {
                      scoreBlurTimer.current = window.setTimeout(() => setScoreActionsOpen(false), 180);
                    }}
                    onChange={(e) => setScore((prev) => ({ ...prev, home: digits2(e.target.value) }))}
                  />
                  <span>:</span>
                  <input
                    className="st-input"
                    maxLength={2}
                    inputMode="numeric"
                    value={score.guest}
                    onFocus={() => {
                      if (scoreBlurTimer.current) window.clearTimeout(scoreBlurTimer.current);
                      setScoreActionsOpen(true);
                    }}
                    onBlur={() => {
                      scoreBlurTimer.current = window.setTimeout(() => setScoreActionsOpen(false), 180);
                    }}
                    onChange={(e) => setScore((prev) => ({ ...prev, guest: digits2(e.target.value) }))}
                  />
              </div>
                <div className="gm-scoreboard-team is-guest">
                  <img src={getTeamImageUrl(match.guest_team?.image) || defaultTeamLogo} alt="" />
                  <span className="gm-scoreboard-name">{guestName}</span>
              </div>
            </div>
              <IfCanWrite entity="matches">
                {scoreActionsOpen ? (
                  <div
                    className="gm-score-actions"
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <button type="button" className="st-btn st-btn-primary" disabled={savingKey === 'score' || savingKey === 'play'} onClick={() => saveScore({ finish: true })}>
                      {t('gmSaveScoreAndFinish')}
                    </button>
                    <button type="button" className="st-btn st-btn-secondary" disabled={savingKey === 'score' || savingKey === 'play'} onClick={() => saveScore()}>
                      {t('gmSaveScore')}
                    </button>
          </div>
                ) : null}
              </IfCanWrite>
              {match.penalties ? (
                <div className="gm-muted" style={{ textAlign: 'center', marginBottom: 8 }}>
                  {t('gmPenaltiesTitle')}: {match.penalties.home_team_scores}:{match.penalties.guest_team_scores}
                </div>
              ) : null}
              <IfCanWrite entity="matches">
                {statsOpen ? (
                  <>
                    <div className="gm-stats">
                      {statistics.map((row) => (
                        <div className="gm-stat-row" key={row.statistic_id}>
            <input
                            className="st-input"
                            maxLength={isPossessionStatistic(row) ? 3 : 2}
                            inputMode="numeric"
                            value={row.home_team_value}
                            onChange={(e) => setStatistics((prev) => prev.map((item) => (
                              item.statistic_id === row.statistic_id ? nextStatisticRow(item, 'home', e.target.value) : item
                            )))}
                            onBlur={() => resetMobileViewport()}
                          />
                          <div className="gm-stat-name">{row.name}</div>
                          <input
                            className="st-input"
                            maxLength={isPossessionStatistic(row) ? 3 : 2}
                            inputMode="numeric"
                            value={row.guest_team_value}
                            onChange={(e) => setStatistics((prev) => prev.map((item) => (
                              item.statistic_id === row.statistic_id ? nextStatisticRow(item, 'guest', e.target.value) : item
                            )))}
                            onBlur={() => resetMobileViewport()}
                          />
          </div>
                      ))}
        </div>
                    <button type="button" className="st-btn st-btn-primary" style={{ width: '100%', marginTop: 8 }} disabled={savingKey === 'stats'} onClick={saveStatistics}>{t('gmSaveStatistics')}</button>
                  </>
                ) : (
                  <button type="button" className="st-btn st-btn-secondary" style={{ width: '100%' }} onClick={() => setStatsOpen(true)}>{t('gmEnterStatistics')}</button>
                )}
              </IfCanWrite>
              {commentOpen ? (
                <div className="gm-comment-field">
                  <textarea
                    className="st-textarea"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder={t('gmCommentPlaceholder')}
                  />
                  <button
                    type="button"
                    className="gm-comment-clear"
                    disabled={!comment}
                    aria-label={t('gmClearComment')}
                    onClick={() => setComment('')}
                  >
                    <FiX />
                  </button>
      </div>
              ) : null}
              <IfCanWrite entity="matches">
                <div className="gm-card-actions" style={{ marginTop: 10 }}>
                  <button
                    type="button"
                    className="st-btn st-btn-secondary"
                    disabled={savingKey === 'state'}
                    onClick={() => (commentOpen ? saveComment() : setCommentOpen(true))}
                  >
                    {commentOpen ? t('gmSaveComment') : t('comment')}
                  </button>
                  <button type="button" className="st-btn st-btn-secondary" onClick={() => { setTechScore({ home: '', guest: '' }); setTechOpen(true); }}>{t('mrTechLose')}</button>
                  <button
                    type="button"
                    className="st-btn st-btn-secondary"
                    disabled={savingKey === 'state'}
                    onClick={() => patchMatchState(
                      { rescheduled: !isPostponed(match) },
                      isPostponed(match) ? 'gmUnpostpone' : 'gmMarkPostponed'
                    )}
                  >
                    {isPostponed(match) ? t('gmUnpostpone') : t('gmMarkPostponed')}
                  </button>
                  {matchFlags.penaltiesAvailable ? (
                    <button type="button" className="st-btn st-btn-secondary" onClick={() => setPenaltiesOpen(true)}>
                      {match.penalties ? t('gmEditPenalties') : t('gmSetPenalties')}
                    </button>
                  ) : null}
            </div>
              </IfCanWrite>
            </div>

            {canWrite ? (
              <div className="st-block gm-play-block">
                <h2 className="st-block-header">{t('gmPlayState')}</h2>
                <select
                  className="st-select gm-play-select"
                  value={playValue}
                  disabled={savingKey === 'play'}
                  onChange={(e) => {
                    const nextPhase = e.target.value;
                    if (nextPhase === playValue || nextPhase === 'postponed' || (nextPhase === 'finished' && playPhase === 'extra_ended')) return;
                    if (nextPhase === 'tech_defeat') {
                      setTechScore({ home: '', guest: '' });
                      setTechOpen(true);
                      return;
                    }
                    setPlayState(
                      playConditionForPhase(nextPhase, playPhase, match),
                      nextPhase === 'first_half' ? { scoreOverride: { home: '0', guest: '0' } } : undefined
                    );
                  }}
                >
                  {playPhase === 'postponed' ? <option value="postponed">{t('matchPostponed')}</option> : null}
                  {playOptions.map((item) => (
                    <option key={item.value} value={item.value}>{t(item.labelKey)}</option>
                  ))}
                </select>
            </div>
            ) : null}

            <div className="st-block gm-flags-block">
              <h2 className="st-block-header">{t('gmMatchFlags')}</h2>
              <div className="gm-flags">
                <div className="gm-flag-extra gm-flag-half-time">
                  <label>{t('gmHalfTime')}</label>
                  <input
                    className="st-input"
                    inputMode="numeric"
                    disabled={!canWrite || savingKey === 'half_time'}
                    value={matchFlags.halfTime}
                    onChange={(e) => setMatchFlags((prev) => ({ ...prev, halfTime: digits2(e.target.value) }))}
                    onBlur={(e) => canWrite && saveHalfTime(e.target.value)}
                  />
                </div>
                <label className="gm-flag">
                  <input
                    type="checkbox"
                    checked={matchFlags.extraTimeAvailable}
                    disabled={!canWrite || savingKey === 'state'}
                    onChange={(e) => saveMatchFlags(
                      { ...matchFlags, extraTimeAvailable: e.target.checked },
                      {
                        extra_time_available: e.target.checked,
                        extra_half_time: e.target.checked ? (Number(matchFlags.extraHalfTime) || 15) : null,
                      }
                    )}
                  />
                  <span>{t('gmExtraTime')}</span>
                </label>
                {matchFlags.extraTimeAvailable ? (
                  <div className="gm-flag-extra">
                    <label>{t('gmExtraHalfTime')}</label>
                    <input
                      className="st-input"
                      inputMode="numeric"
                      disabled={!canWrite || savingKey === 'state'}
                      value={matchFlags.extraHalfTime}
                      onChange={(e) => setMatchFlags((prev) => ({ ...prev, extraHalfTime: digits2(e.target.value) }))}
                      onBlur={(e) => canWrite && saveMatchFlags(
                        { ...matchFlags, extraHalfTime: digits2(e.target.value) },
                        {
                          extra_time_available: true,
                          extra_half_time: Number(digits2(e.target.value)) || 15,
                        }
                      )}
                    />
            </div>
                ) : null}
                <label className="gm-flag">
                  <input
                    type="checkbox"
                    checked={matchFlags.penaltiesAvailable}
                    disabled={!canWrite || savingKey === 'state'}
                    onChange={(e) => saveMatchFlags(
                      { ...matchFlags, penaltiesAvailable: e.target.checked },
                      { penalty_kicks_available: e.target.checked }
                    )}
                  />
                  <span>{t('gmPenaltiesAvailable')}</span>
                </label>
                <label className="gm-flag">
                  <input
                    type="checkbox"
                    checked={matchFlags.timelineEnabled}
                    disabled={!canWrite || savingKey === 'state'}
                    onChange={(e) => saveMatchFlags(
                      { ...matchFlags, timelineEnabled: e.target.checked },
                      { timeline_disabled: e.target.checked ? null : 1 }
                    )}
                  />
                  <span>
                    {t('gmEnableTimeline')}
                    <span className="gm-flag-hint">{t('gmTimelineHint')}</span>
                  </span>
                </label>
            </div>
            </div>

            <div className="st-block">
              <h2 className="st-block-header">{t('gmTournament')}</h2>
              <div className="st-form-grid">
                <div className="st-field">
                  <label>{t('gmCountry')}</label>
                  <select className="st-select" value={tournamentForm.country_id} onChange={async (e) => {
                    const country_id = e.target.value;
                    const nextLeagues = country_id ? await fetchLeagues(country_id) : [];
                    setLeagues(nextLeagues);
                    setSeasons([]);
                    setStages([]);
                    setTournamentForm((prev) => ({ ...prev, country_id, league_id: '', season_id: '', stage_id: '', net_position: '' }));
                  }}>
                    <option value="">{t('trCountryPlaceholder')}</option>
                    {countries.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                  </select>
            </div>
                <div className="st-field">
                  <label>{t('league')}</label>
                  <select className="st-select" value={tournamentForm.league_id} onChange={async (e) => {
                    const league_id = e.target.value;
                    const nextSeasons = league_id ? await fetchSeasons(league_id) : [];
                    setSeasons(nextSeasons);
                    setStages([]);
                    setTournamentForm((prev) => ({ ...prev, league_id, season_id: '', stage_id: '', net_position: '' }));
                  }}>
                    <option value="">{t('leaguePlaceholder')}</option>
                    {leagues.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                  </select>
            </div>
                <div className="st-field">
                  <label>{t('stSeason')}</label>
                  <select className="st-select" value={tournamentForm.season_id} onChange={async (e) => {
                    const season_id = e.target.value;
                    const nextStages = season_id ? await fetchStages(tournamentForm.league_id, season_id) : [];
                    setStages(nextStages);
                    setTournamentForm((prev) => ({ ...prev, season_id, stage_id: '', net_position: '' }));
                  }}>
                    <option value="">{t('stSeasonPlaceholder')}</option>
                    {seasons.map((item) => <option key={item.id || item.season_id} value={item.id || item.season_id}>{item.name || item.season_name}</option>)}
                  </select>
            </div>
                <div className="st-field">
                  <label>{t('gmStage')}</label>
                  <select
                    className="st-select"
                    value={tournamentForm.stage_id}
                    onChange={(e) => setTournamentForm((prev) => ({
                      ...prev,
                      stage_id: e.target.value,
                      net_position: '',
                    }))}
                  >
                    <option value="">{t('mmStagePlaceholder')}</option>
                    {stages.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                  </select>
              </div>
                {(() => {
                  const selectedStage = stages.find((item) => Number(item.id) === Number(tournamentForm.stage_id));
                  const stagePlayoff = isPlayoffStage(selectedStage) || isPlayoffStage(match?.stage);
                  if (!stagePlayoff || !tournamentForm.stage_id) return null;
                  const stageTitle = selectedStage?.name
                    || selectedStage?.short_name
                    || match?.stage_name
                    || '';
                  const slotCount = estimatePlayoffExpectedSlots(
                    stageTitle,
                    selectedStage || match?.stage
                  );
                  const pairingHint = playoffNetPositionPairingHint(
                    stageTitle,
                    slotCount,
                    selectedStage || match?.stage,
                    lang
                  );
                  return (
                    <div className="st-field">
                      <label>{t('cmNetPosition')}</label>
                      <select
                        className="st-select"
                        value={tournamentForm.net_position}
                        onChange={(e) => setTournamentForm((prev) => ({ ...prev, net_position: e.target.value }))}
                      >
                        <option value="">{t('cmNetPositionClear')}</option>
                        {Array.from({ length: slotCount }, (_, index) => index + 1).map((slot) => (
                          <option key={`edit-net-${slot}`} value={slot}>
                            {formatPlayoffNetPositionLabel(
                              stageTitle,
                              slot,
                              selectedStage || match?.stage,
                              lang
                            )}
                          </option>
                        ))}
                      </select>
                      <p className="st-hint" style={{ margin: '6px 0 0' }}>
                        {pairingHint || t('cmNetPositionHint')}
                      </p>
                    </div>
                  );
                })()}
              </div>
              <IfCanWrite entity="matches">
                <div className="st-actions"><button type="button" className="st-btn st-btn-primary" disabled={savingKey === 'tournament'} onClick={saveTournament}>{t('gmSaveChanges')}</button></div>
              </IfCanWrite>
                </div>

            <div className="st-block">
              <h2 className="st-block-header">{t('gmPlace')}</h2>
              <div className="st-form-grid">
                <div className="st-field">
                  <label>{t('stadium')}</label>
                  <select className="st-select" value={placeForm.stadium_id} onChange={(e) => {
                    const stadium_id = e.target.value;
                    setPlaceForm((prev) => ({ ...prev, stadium_id, pitch_id: '' }));
                    loadPitches(stadium_id);
                  }}>
                    <option value="">{t('stadium')}</option>
                    {stadiums.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                  </select>
                </div>
                <div className="st-field">
                  <label>{t('gmPitch')}</label>
                  <select className="st-select" value={placeForm.pitch_id} onChange={(e) => setPlaceForm((prev) => ({ ...prev, pitch_id: e.target.value }))}>
                    <option value="">{t('gmPitchPlaceholder')}</option>
                    {pitches.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                  </select>
                </div>
                <div className="st-field">
                  <label>{t('gmDate')}</label>
                  <input className="st-input" type="date" value={placeForm.date} onChange={(e) => setPlaceForm((prev) => ({ ...prev, date: e.target.value }))} />
                </div>
                <div className="st-field">
                  <label>{t('time')}</label>
                  <input
                    className="st-input"
                    type="text"
                    inputMode="numeric"
                    placeholder="12:00"
                    autoComplete="off"
                    value={placeForm.time}
                    onChange={(e) => {
                      const next = e.target.value.replace(/[^\d:.\-–—]/g, '').slice(0, 5);
                      setPlaceForm((prev) => ({ ...prev, time: next }));
                    }}
                    onBlur={() => {
                      setPlaceForm((prev) => {
                        const normalized = normalizePartialKickoffTime(prev.time);
                        return normalized === prev.time ? prev : { ...prev, time: normalized };
                      });
                    }}
                  />
                </div>
              </div>
              <IfCanWrite entity="matches">
                <div className="st-actions"><button type="button" className="st-btn st-btn-primary" disabled={savingKey === 'place'} onClick={savePlace}>{t('gmSaveChanges')}</button></div>
              </IfCanWrite>
            </div>

            <div className="st-block">
              <div className="gm-card-header">
                <h2 className="st-block-header">{t('gmStaff')}</h2>
                <IfCanWrite entity="matches">
                  <button type="button" className="st-btn st-btn-secondary" onClick={openStaffPicker}><FiPlus /> {t('gmAddStaff')}</button>
                </IfCanWrite>
                          </div>
              {(staffDraft?.staff || []).length ? staffDraft.staff.map((person) => (
                <div className="gm-staff-row" key={`${person.id}-${person.slot}`}>
                  <div className="gm-staff-person">
                    <img src={staffPhotoUrl(person) || defaultAvatar} alt="" />
                    <div>
                      <strong>{staffDisplayName(person)}</strong>
                      {person.login && person.login !== staffDisplayName(person) ? (
                        <div className="gm-staff-login">{person.login}</div>
                      ) : null}
                      <div className="gm-muted">{staffRoleText(person)}</div>
                    </div>
              </div>
                  {canWrite ? (
                    <button type="button" className="st-icon-btn" onClick={() => {
                      const next = {
                        ...staffDraft,
                        staff: staffDraft.staff.filter((item) => !(item.slot === person.slot && item.id === person.id)),
                      };
                      ALL_STAFF_SLOTS.forEach((slot) => {
                        next[slot] = next.staff.find((item) => item.slot === slot)?.id || null;
                      });
                      setStaffDraft(next);
                      saveStaff(next);
                    }}><FiTrash2 /></button>
                  ) : null}
            </div>
              )) : <div className="gm-muted">{t('gmNoStaff')}</div>}
          </div>

            <div className="st-block">
              <div className="gm-card-header">
                <h2 className="st-block-header">{t('gmVideos')}</h2>
                <IfCanWriteMatchMedia>
                  {isAddingVideo ? null : (
                    <button type="button" className="st-btn st-btn-secondary" onClick={() => openVideoForm()}><FiPlus /> {t('gmAddVideo')}</button>
                  )}
                </IfCanWriteMatchMedia>
              </div>
              {isAddingVideo ? renderVideoForm() : null}
              {(match.videos || []).length ? match.videos.map((video) => (
                isEditingVideo(video) ? (
                  <div key={video.id}>{renderVideoForm()}</div>
                ) : (
                  <div
                    className={`gm-video-row${canWriteMedia ? ' is-editable' : ''}`}
                    key={video.id}
                    onClick={canWriteMedia ? () => openVideoForm(video) : undefined}
                  >
                    <div className="gm-video-main">
                      <VideoPreviewThumb preview={video.preview || null} href={video.video || ''} />
                      <div className="gm-video-copy">
                        <div className="gm-video-type">{videoTypeLabel(video, t)}</div>
                        {video.name ? <div className="gm-video-name" title={String(video.name).trim()}>{videoNamePreview(video.name)}</div> : null}
                        <div className="gm-muted" title={video.video}>{video.video}</div>
                      </div>
                    </div>
                    {canWriteMedia ? (
                      <button
                        type="button"
                        className="st-icon-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteVideo(video);
                        }}
                      >
                        <FiTrash2 />
                      </button>
                    ) : null}
                  </div>
                )
              )) : (isAddingVideo ? null : <div className="gm-muted">{t('gmNoVideos')}</div>)}
            </div>

            <div className="st-block">
              <h2 className="st-block-header">{t('gmPhotos')}</h2>
              {(match.photo_set?.album_name || match.photo_set?.group_name) ? (
                <div className="gm-photoset-meta">
                  {match.photo_set.album_name ? (
                    match.photoSetUrl ? (
                      <a
                        className="gm-photoset-album"
                        href={match.photoSetUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={match.photo_set.album_name}
                      >
                        {match.photo_set.album_name}
                      </a>
                    ) : (
                      <div className="gm-photoset-album" title={match.photo_set.album_name}>{match.photo_set.album_name}</div>
                    )
                  ) : null}
                  {match.photo_set.group_name ? <div className="gm-muted">{match.photo_set.group_name}</div> : null}
                </div>
              ) : null}
              <div className="st-field">
                <label>{t('gmAddPhotoSet')}</label>
                <input
                  className={`st-input${photoUrlError ? ' error' : ''}`}
                  value={match.photoSetUrl || ''}
                  disabled={!canWriteMedia}
                  readOnly={!canWriteMedia}
                  onChange={(e) => {
                    if (!canWriteMedia) return;
                    setPhotoUrlError('');
                    setMatch((prev) => ({ ...prev, photoSetUrl: e.target.value, photo_set: null }));
                  }}
                  placeholder={t('mmVkPh')}
                />
                {photoUrlError ? <div className="st-message error">{photoUrlError}</div> : null}
          </div>
              <IfCanWriteMatchMedia>
                <div className="gm-card-actions" style={{ marginTop: 8 }}>
                  <button type="button" className="st-btn st-btn-primary" disabled={savingKey === 'photos'} onClick={savePhotoSet}>{t('gmSavePhotoSet')}</button>
                  <button type="button" className="st-btn st-btn-ghost" disabled={savingKey === 'photos'} onClick={deletePhotoSet}>{t('gmDeletePhotoSet')}</button>
            </div>
              </IfCanWriteMatchMedia>
            </div>
          </div>

            <div className="gm-match-main">
            <div className="st-block">
              <div className="gm-card-header">
                <h2 className="st-block-header">{t('gmEvents')}</h2>
                <div className="gm-card-actions">
                  <div className="gm-menu-wrap" ref={protocolMenuRef}>
                    <button type="button" className="st-btn st-btn-ghost gm-mini-btn" onClick={() => setProtocolMenuOpen((value) => !value)}>
                      <FiFileText /> {t('gmProtocol')}
                    </button>
                    {protocolMenuOpen ? (
                      <div className="gm-menu">
                        <button
                          type="button"
                          className="gm-menu-item"
                          onClick={() => { setProtocolMenuOpen(false); openMatchProtocol(matchId); }}
                        >
                          <strong>{t('gmProtocolEmpty')}</strong>
                          <span className="gm-muted">{t('gmProtocolEmptyHint')}</span>
                        </button>
                        <button
                          type="button"
                          className="gm-menu-item"
                          onClick={() => { setProtocolMenuOpen(false); openMatchProtocol(matchId, true); }}
                        >
                          <strong>{t('gmProtocolFilled')}</strong>
                          <span className="gm-muted">{t('gmProtocolFilledHint')}</span>
                        </button>
                      </div>
                    ) : null}
                  </div>
                  <button type="button" className="st-btn st-btn-ghost gm-mini-btn" onClick={() => setProtocolMode((value) => !value)}>
                    {protocolMode ? t('gmFillEvents') : t('gmFillProtocol')}
                  </button>
                  {!protocolMode && canWrite ? (
                    <button type="button" className="st-btn st-btn-secondary gm-mini-btn" onClick={() => setEventEditor(newEventForm({ team_id: match.home_team_id }))}>
                      <FiPlus /> {t('gmAddEvent')}
                    </button>
                  ) : null}
                  {protocolMode && canWrite ? (
                    <button type="button" className="st-btn st-btn-primary gm-mini-btn" disabled={savingKey === 'protocol'} onClick={submitProtocol}>
                      {t('gmSubmitProtocol')}
                    </button>
                  ) : null}
        </div>
            </div>

              {protocolMode ? (
                <div className="gm-protocol-note">{t('gmProtocolTimeNote')}</div>
              ) : null}

              {protocolMode ? (
                <div className="gm-protocol">
                  {[match.home_team_id, match.guest_team_id].map((teamId) => (
                    <div key={teamId} className="gm-protocol-team">
                      <h3>{teamName(teamId)}</h3>
                      <div className="table-responsive">
                        <table className="table">
                          <thead>
                            <tr>
                              <th>№</th>
                              <th>{t('player')}</th>
                              <th>{t('mrPresence')}</th>
                              <th>{t('goal')}</th>
                              <th>{t('assist')}</th>
                              <th>{t('mrOG')}</th>
                              <th>{t('mrYC')}</th>
                              <th>{t('mrRC')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {roster.filter((player) => Number(player.team_id) === Number(teamId) || Number(player.isDouble) === Number(teamId)).map((player) => (
                              <tr
                                key={player.id}
                                className={canWrite ? 'gm-protocol-row' : ''}
                                onMouseDown={(e) => startPlayerPress(e, player)}
                                onMouseUp={cancelPlayerPress}
                                onMouseLeave={cancelPlayerPress}
                                onTouchStart={(e) => startPlayerPress(e, player)}
                                onTouchEnd={cancelPlayerPress}
                                onTouchMove={cancelPlayerPress}
                                onContextMenu={(e) => openPlayerMenu(e, player)}
                              >
                                <td>
                                  {canWrite ? (
                                    <input
                                      className="st-input gm-protocol-number"
                                      placeholder="№"
                                      value={player.match_number}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      onTouchStart={(e) => e.stopPropagation()}
                                      onContextMenu={(e) => e.stopPropagation()}
                                      onChange={(e) => setRoster((prev) => prev.map((item) => item.id === player.id ? { ...item, match_number: e.target.value.replace(/[^0-9]/g, '') } : item))}
                                    />
                                  ) : (player.match_number || player.player_number || '')}
                                </td>
                                <td>
                                  {playerDisplayName(player)}
                                  {player.mvp ? <span className="gm-protocol-tag">MVP</span> : null}
                                  {isMatchKeeper(player) ? <span className="gm-protocol-tag">{t('mrKeeperTag')}</span> : null}
                                  {Number(player.isDouble) === Number(teamId) ? <span className="gm-protocol-tag">{t('mrDouble')}</span> : null}
                                  {player.from_captain ? <span className="gm-protocol-tag is-warn">{t('gmCaptainLineup')}</span> : null}
                                </td>
                                <td>
                                  <input type="checkbox" checked={Boolean(player.active)} onChange={() => toggleRosterPlayer(player, teamId)} />
                                </td>
                                {['goals', 'assists', 'autogoals', 'yellow_cards', 'red_cards'].map((field) => (
                                  <td key={field}>
                                    <input
                                      className="matchColumnInput"
                                      value={player[field] || ''}
                                      onChange={(e) => {
                                        const value = e.target.value.replace(/[^0-9]/g, '');
                                        setRoster((prev) => prev.map((item) => item.id === player.id ? { ...item, [field]: value, active: item.active || Boolean(value) } : item));
                                      }}
                                    />
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
              </div>
              </div>
                  ))}
            </div>
              ) : null}

              {protocolMode ? (
                <div className="gm-protocol-footer">
                  <div className="gm-protocol-legend">
                    <div>
                      <b>{t('mrPresence')}</b> — {t('mrHintPresence')} · <b>{t('goal')}</b> — {t('mrHintGoals')} · <b>{t('assist')}</b> — {t('mrHintAssists')} · <b>{t('mrOG')}</b> — {t('mrHintOG')} · <b>{t('mrYC')}</b> — {t('mrHintYC')} · <b>{t('mrRC')}</b> — {t('mrHintRC')}
                    </div>
                    <div>
                      <b>MVP</b> — {t('gmLegendMvp')} · <b>{t('mrKeeperTag')}</b> — {t('gmLegendKeeper')} · <b>{t('mrDouble')}</b> — {t('gmLegendDouble')}
                    </div>
                    <IfCanWrite entity="matches">
                      <div>{t('gmLongPressHint')}</div>
                    </IfCanWrite>
                  </div>
                  <IfCanWrite entity="matches">
                    <button type="button" className="st-btn st-btn-primary" disabled={savingKey === 'protocol'} onClick={submitProtocol}>{t('gmSubmitProtocol')}</button>
                  </IfCanWrite>
                </div>
              ) : null}

              {!protocolMode && canWrite && onlineLive ? (
                <OnlineClockBar matchId={matchId} hotkey={!eventEditor} onOpenOnline={() => setOnlineOpen(true)} />
              ) : null}

              {protocolMode ? null : (
                <div className="gm-events-toolbar">
                  {canWrite ? (
                    <div className="gm-quick-events-wrap">
                      <div className="gm-quick-events" role="group" aria-label={t('gmQuickEvents')}>
                        {QUICK_EVENT_HOTKEYS.map((item) => (
                          <button
                            key={item.id}
                            type="button"
                            className="gm-quick-event-btn"
                            title={`${t(item.labelKey)} (${item.hint})`}
                            onClick={() => openQuickEvent(item.id)}
                          >
                            <EventGlyph id={item.id} />
                            <span>{t(item.labelKey)}</span>
                            <kbd className="gm-quick-kbd">{item.hint}</kbd>
                          </button>
                        ))}
                      </div>
                      <div className="gm-quick-events-hint">{t('gmQuickHotkeysHint')}</div>
                    </div>
                  ) : <span />}
                  <label className="gm-events-filter">
                    <input
                      type="checkbox"
                      checked={mainEventsOnly}
                      onChange={(e) => setMainEventsOnly(e.target.checked)}
                    />
                    <span>{t('gmMainEventsOnly')}</span>
                  </label>
                </div>
              )}

              {protocolMode ? null : (
                timelineEvents.length ? timelineEvents.map((event, index) => (
                  <div
                    key={event.id || event.server_id || `event-${event.event_id}-${event.minute}-${index}`}
                    className={`gm-event-row${event.synthetic ? ' is-system' : ''}`}
                    onClick={() => canWrite && !event.synthetic && setEventEditor({
                      ...event,
                      previous_event_id: event.event_id,
                      previous_team_id: event.team_id,
                      minute: event.minute === 0 || event.minute ? String(event.minute) : '',
                      second: event.second === 0 || event.second ? String(event.second).padStart(2, '0') : '',
                      time_hint: { minute: event.minute, second: event.second, time_number: event.time_number },
                    })}
                  >
                    <span className="gm-event-min">{formatEventMinuteLabel(event.minute, event.second)}'</span>
                    <span className="gm-event-glyph"><EventGlyph id={event.event_id} /></span>
                    <div className="gm-event-body">
                      <div className="gm-event-line">
                        <span className="gm-event-author">
                          {renderEventPerson(event, 'player') || (event.synthetic ? '' : '—')}
                        </span>
                        <span className="gm-event-type">{eventLabel(event, t)}</span>
                      </div>
                      {eventPersonParts(event, 'assist').name || eventPersonParts(event, 'assist').number ? (
                        <div className="gm-event-assist">
                          {t(eventTypeById(event.event_id)?.secondLabelKey || 'assist')}: {renderEventPerson(event, 'assist')}
                        </div>
                      ) : null}
                      {event.comment ? <div className="gm-event-note">{event.comment}</div> : null}
                    </div>
                    {event.team_id ? (
                      <span className="gm-event-club" title={event.team_name || teamName(event.team_id)}>
                        <img
                          className="gm-event-logo"
                          src={getTeamImageUrl(eventTeam(event)?.image || event.team_image) || defaultTeamLogo}
                          alt=""
                          onError={(e) => { e.currentTarget.src = defaultTeamLogo; }}
                        />
                        <span className="gm-muted">{teamAbbrev(eventTeam(event), event.team_name || teamName(event.team_id))}</span>
                      </span>
                    ) : <span />}
          </div>
                )) : <div className="gm-muted">{t('gmNoEvents')}</div>
              )}
            </div>

            {serverSetup ? (
              <div className="st-block">
                <div className="gm-card-header">
                  <h2 className="st-block-header">{t('gmLineupSetup')}</h2>
                  <IfCanWrite entity="matches">
                    <button type="button" className="st-btn st-btn-ghost gm-mini-btn" onClick={openLineupSetup}>
                      {t('gmEditSetup')}
                    </button>
                  </IfCanWrite>
                </div>
                {captainLineupPending ? <div className="gm-cap-banner">{t('gmCaptainLineupHint')}</div> : null}
                <div
                  className={`gm-formations${canWrite ? ' is-clickable' : ''}`}
                  onClick={canWrite ? openLineupSetup : undefined}
                >
                  <MatchFormationPreview
                    teamName={homeName}
                    teamLogo={getTeamImageUrl(match.home_team?.image) || defaultTeamLogo}
                    formation={serverSetup.homeFormation}
                    lineup={serverSetup.homeLineup}
                    roster={roster}
                    color={serverSetup.homeColor}
                    fromCaptain={homeCaptainPending || serverSetup.homeFromCaptain}
                  />
                  <MatchFormationPreview
                    teamName={guestName}
                    teamLogo={getTeamImageUrl(match.guest_team?.image) || defaultTeamLogo}
                    formation={serverSetup.guestFormation}
                    lineup={serverSetup.guestLineup}
                    roster={roster}
                    color={serverSetup.guestColor}
                    fromCaptain={guestCaptainPending || serverSetup.guestFromCaptain}
                  />
                </div>
              </div>
            ) : null}

            </div>

            <div className="gm-match-aside">
            <div className="st-block">
              <div className="gm-card-header">
                <h2 className="st-block-header">{t('gmLineups')}</h2>
                <IfCanWrite entity="matches">
                  <div className="gm-card-actions">
                    <button type="button" className="st-btn st-btn-ghost gm-mini-btn gm-save-btn" onClick={openLineupSetup}>
                      {t('gmLineupSetup')}
                    </button>
                    <button
                      type="button"
                      className={`st-btn ${lineupDirty || captainLineupPending ? 'st-btn-primary' : 'st-btn-ghost'} gm-mini-btn gm-save-btn`}
                      disabled={(!lineupDirty && !captainLineupPending) || savingKey === 'lineup'}
                      onClick={saveLineup}
                    >
                      {captainLineupPending && !lineupDirty ? t('gmApproveLineup') : t('gmSaveLineup')}
                    </button>
                  </div>
                </IfCanWrite>
              </div>
              <div className="gm-lineup-toggle">
                <button type="button" className={`gm-chip ${lineupSide === 'home' ? 'is-active' : ''} ${homeCaptainPending ? 'is-pending' : ''}`} onClick={() => setLineupSide('home')}>{homeName || t('gmHome')}</button>
                <button type="button" className={`gm-chip ${lineupSide === 'guest' ? 'is-active' : ''} ${guestCaptainPending ? 'is-pending' : ''}`} onClick={() => setLineupSide('guest')}>{guestName || t('gmGuest')}</button>
                <IfCanWrite entity="matches">
                  <button type="button" className="gm-select-all" onClick={toggleAllActive}>
                    {allVisibleActive ? t('olmDeselectAll') : t('olmSelectAll')}
                  </button>
                </IfCanWrite>
            </div>
              {captainLineupPending ? <div className="gm-cap-banner">{t('gmCaptainLineupHint')}</div> : null}
              {visibleRoster.map((player) => (
                <div
                  key={player.id}
                  className={`gm-player ${player.active ? '' : 'is-bench'}`}
                  onClick={() => toggleRosterPlayer(player, lineupSide === 'home' ? match?.home_team_id : match?.guest_team_id)}
                >
                  <img src={getPlayerImageUrl(player.image) || defaultAvatar} alt="" />
                  <div className="gm-player-name">
                    <strong title={playerDisplayName(player)}>{playerShortName(player) || playerDisplayName(player)}</strong>
                    <span className="gm-muted">{positionLabel(player, t) || t('gmNotListed')}</span>
                    {player.from_captain && player.active ? <span className="gm-cap-tag">{t('gmCaptainLineup')}</span> : null}
              </div>
                  <span className="gm-muted">{positionLabel(player, t)}</span>
                  <div className="gm-player-events">
                    {playerEvents(player.id).map((event) => (
                      <span key={event.id || `${event.event_id}-${event.minute}`}><EventGlyph id={event.event_id} /></span>
                    ))}
              </div>
                  <input
                    className="st-input"
                    placeholder="№"
                    value={player.match_number}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setRoster((prev) => prev.map((item) => item.id === player.id ? { ...item, match_number: e.target.value.replace(/[^0-9]/g, '') } : item))}
                  />
                  <button
                    type="button"
                    className={`gm-keeper ${isMatchKeeper(player) ? 'is-on' : ''}`}
                    title={t('mrGoalkeeper')}
                    onClick={(e) => { e.stopPropagation(); toggleKeeper(player); }}
                  >
                    <PiHandPalmBold />
                  </button>
                  <button type="button" className={`gm-star ${player.mvp ? 'is-on' : ''}`} onClick={(e) => { e.stopPropagation(); toggleMvp(player); }}>★</button>
            </div>
              ))}
          </div>
            </div>
              </div>
                </div>
              </div>

      {setupOpen ? (
        <div className="olm-modal-overlay">
          <div className="olm-modal-content">
            <div className="olm-container">
              <div className="olm-header">
                <h2 className="olm-title">{t('gmLineupSetup')}</h2>
                <span className="olm-header-info">{homeName} — {guestName}</span>
                <div className="olm-header-close" onClick={() => setSetupOpen(false)}>✕</div>
              </div>
              {setupNotice ? <div className="olm-notification">{setupNotice}</div> : null}
              <div className="olm-body">
                <MatchLineupSetup
                  step={setupStep}
                  onStepChange={setSetupStep}
                  matchData={{
                    ...match,
                    home_team_name: homeName,
                    guest_team_name: guestName,
                    home_team_image: match.home_team?.image,
                    guest_team_image: match.guest_team?.image,
                  }}
                  matchApplication={roster}
                  value={lineupSetup}
                  onChange={(patch) => {
                    setupTouchedRef.current = true;
                    if (patch.selectedPlayers) {
                      const prevSelected = lineupSetup.selectedPlayers || {};
                      const nextSelected = patch.selectedPlayers;
                      const changedId = Object.keys(nextSelected).find((id) => Boolean(nextSelected[id]) !== Boolean(prevSelected[id]));
                      const changed = roster.find((item) => String(item.id) === String(changedId));
                      if (changed) dropCaptainMarkForTeam(changed.isDouble || changed.team_id);
                    }
                    setLineupSetup((prev) => ({ ...prev, ...patch }));
                  }}
                  onCancel={() => setSetupOpen(false)}
                  onSubmit={submitLineupSetup}
                  submitLabel={captainLineupPending ? t('gmApproveLineup') : t('gmSaveSetup')}
                  notify={showSetupNotice}
                  homeFromCaptain={homeCaptainPending}
                  guestFromCaptain={guestCaptainPending}
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {playerMenu ? (
        <>
          <div className="gm-context-backdrop" onClick={() => setPlayerMenu(null)} onContextMenu={(e) => { e.preventDefault(); setPlayerMenu(null); }} />
          <div className="gm-menu gm-context-menu" style={{ top: playerMenu.y, left: playerMenu.x }}>
            <div className="gm-context-title">{playerDisplayName(playerMenu.player)}</div>
            <button
              type="button"
              className="gm-menu-item"
              onClick={() => { const player = playerMenu.player; setPlayerMenu(null); toggleMvp(player); }}
            >
              <strong>{playerMenu.player.mvp ? t('gmUnmarkMvp') : t('gmMarkMvp')}</strong>
            </button>
            <button
              type="button"
              className="gm-menu-item"
              onClick={() => { const player = playerMenu.player; setPlayerMenu(null); toggleKeeper(player); }}
            >
              <strong>{isMatchKeeper(playerMenu.player) ? t('gmUnmarkKeeper') : t('gmMarkKeeper')}</strong>
            </button>
          </div>
        </>
      ) : null}

      <CustomModal
        className="gm-modal-host"
        visible={deleteMatchOpen}
        closeModal={() => !deletingMatch && setDeleteMatchOpen(false)}
      >
        <div className="gm-modal">
          <h3>{t('trDeleteMatchTitle')}</h3>
          <p>
            {`${homeName} — ${guestName}. ${t('trDeleteMatchText')}`}
          </p>
          <div className="gm-modal-actions">
            <button
              type="button"
              className="st-btn st-btn-ghost"
              disabled={deletingMatch}
              onClick={() => setDeleteMatchOpen(false)}
            >
              {t('cancel')}
            </button>
            <button
              type="button"
              className="st-btn st-btn-danger"
              disabled={deletingMatch}
              onClick={deleteMatch}
            >
              {deletingMatch ? t('mrLoading') : t('delete')}
            </button>
          </div>
        </div>
      </CustomModal>

      <CustomModal className="gm-modal-host" visible={techOpen} closeModal={() => setTechOpen(false)}>
        <div className="gm-modal">
          <h3>{t('mrTechLoseTitle')}</h3>
          <p className="gm-muted" style={{ margin: '0 0 10px', textAlign: 'center' }}>
            {homeName} — {guestName}
          </p>
          <div className="gm-scoreboard gm-modal-scoreboard">
            <div className="gm-scoreboard-team is-home">
              <span className="gm-scoreboard-name">{homeName || t('gmHome')}</span>
            </div>
            <div className="gm-score-inputs">
              <input
                className="st-input"
                maxLength={2}
                inputMode="numeric"
                placeholder="0"
                aria-label={homeName || t('gmHome')}
                value={techScore.home}
                onChange={(e) => setTechScore((prev) => ({ ...prev, home: digits2(e.target.value) }))}
              />
              <span>:</span>
              <input
                className="st-input"
                maxLength={2}
                inputMode="numeric"
                placeholder="0"
                aria-label={guestName || t('gmGuest')}
                value={techScore.guest}
                onChange={(e) => setTechScore((prev) => ({ ...prev, guest: digits2(e.target.value) }))}
              />
            </div>
            <div className="gm-scoreboard-team is-guest">
              <span className="gm-scoreboard-name">{guestName || t('gmGuest')}</span>
            </div>
          </div>
          <div className="gm-tech-presets">
            <button
              type="button"
              className="st-btn st-btn-secondary"
              onClick={() => setTechScore({ home: '5', guest: '0' })}
            >
              {t('mrTechHomeWins')}
            </button>
            <button
              type="button"
              className="st-btn st-btn-secondary"
              onClick={() => setTechScore({ home: '0', guest: '5' })}
            >
              {t('mrTechGuestWins')}
            </button>
          </div>
          <textarea className="st-textarea" style={{ marginTop: 12 }} value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t('mrTechLosePh')} />
          <div className="gm-modal-actions">
            <button type="button" className="st-btn st-btn-ghost" onClick={() => setTechOpen(false)}>{t('cancel')}</button>
            <button type="button" className="st-btn st-btn-primary" disabled={savingKey === 'score' || savingKey === 'play'} onClick={() => saveScore({ condition: 'tech_defeat', scores: techScore })}>{t('add')}</button>
          </div>
        </div>
      </CustomModal>

      <CustomModal className="gm-modal-host" visible={penaltiesOpen} closeModal={() => setPenaltiesOpen(false)}>
        <div className="gm-modal">
          <h3>{t('gmPenaltiesTitle')}</h3>
          <p className="gm-muted" style={{ margin: '0 0 10px', textAlign: 'center' }}>
            {homeName} — {guestName}
          </p>
          <div className="gm-scoreboard gm-modal-scoreboard">
            <div className="gm-scoreboard-team is-home">
              <span className="gm-scoreboard-name">{homeName || t('gmHome')}</span>
            </div>
            <div className="gm-score-inputs">
              <input
                className="st-input"
                maxLength={2}
                inputMode="numeric"
                placeholder="0"
                aria-label={homeName || t('gmHome')}
                value={penaltyScore.home}
                onChange={(e) => setPenaltyScore((prev) => ({ ...prev, home: digits2(e.target.value) }))}
              />
              <span>:</span>
              <input
                className="st-input"
                maxLength={2}
                inputMode="numeric"
                placeholder="0"
                aria-label={guestName || t('gmGuest')}
                value={penaltyScore.guest}
                onChange={(e) => setPenaltyScore((prev) => ({ ...prev, guest: digits2(e.target.value) }))}
              />
            </div>
            <div className="gm-scoreboard-team is-guest">
              <span className="gm-scoreboard-name">{guestName || t('gmGuest')}</span>
            </div>
          </div>
          {match.penalties ? (
            <button
              type="button"
              className="st-btn st-btn-ghost"
              style={{ width: '100%', marginTop: 4 }}
              disabled={savingKey === 'penalties'}
              onClick={deletePenalties}
            >
              {t('gmDeletePenalties')}
            </button>
          ) : null}
          <div className="gm-modal-actions">
            <button type="button" className="st-btn st-btn-ghost" onClick={() => setPenaltiesOpen(false)}>{t('cancel')}</button>
            <button type="button" className="st-btn st-btn-primary" disabled={savingKey === 'penalties'} onClick={savePenalties}>{t('save')}</button>
            </div>
        </div>
      </CustomModal>

      <CustomModal className="gm-modal-host gm-event-modal-host" visible={Boolean(eventEditor)} closeModal={() => setEventEditor(null)}>
        <div className="gm-modal">
          <h3>{eventEditor?.id ? t('gmEditEvent') : t('gmAddEvent')}</h3>
          {eventEditor ? (
            <div className="st-form-grid">
              <div className="st-field">
                <label>{t('mrEventType')}</label>
                <select
                  className="st-select"
                  value={eventEditor.event_id}
                  onChange={(e) => {
                    const nextType = Number(e.target.value);
                    setEventEditor((prev) => ({
                      ...prev,
                      event_id: prev.id
                        ? nextType
                        : resolveYellowCardEventId(nextType, events, prev.player_id, { isEdit: false }),
                    }));
                  }}
                >
                  {editableEventTypes.map((item) => (
                    <option key={item.id} value={item.id}>{eventLabel(item.id, t)}</option>
                  ))}
                </select>
              </div>
              <div className="st-field">
                <label>{t('team')}</label>
                <select className="st-select" value={eventEditor.team_id} onChange={(e) => setEventEditor((prev) => ({ ...prev, team_id: e.target.value, player_id: '', additional_player_id: '' }))}>
                  <option value={match.home_team_id}>{homeName}</option>
                  <option value={match.guest_team_id}>{guestName}</option>
                </select>
              </div>
              <div className="st-field">
                <label>
                  {t('player')}
                  {eventType?.needsPlayer ? <span className="st-required"> *</span> : null}
                </label>
                <select
                  className="st-select"
                  value={eventEditor.player_id}
                  onChange={(e) => {
                    const playerId = e.target.value;
                    setEventEditor((prev) => {
                      if (prev.id || Number(prev.event_id) !== YELLOW_CARD_EVENT_ID) {
                        return { ...prev, player_id: playerId };
                      }
                      return {
                        ...prev,
                        player_id: playerId,
                        event_id: resolveYellowCardEventId(
                          YELLOW_CARD_EVENT_ID,
                          events,
                          playerId,
                          { isEdit: false },
                        ),
                      };
                    });
                  }}
                >
                    <option value="">{eventType?.needsPlayer ? t('olmSelectPlayer') : t('mrSelect')}</option>
                  {eventTeamPlayers(eventEditor.team_id, eventEditor.player_id).map((player) => {
                    const num = playerShirtNumber(player);
                    const name = playerDisplayName(player);
                    return (
                      <option key={player.id} value={player.id}>
                        {num ? `#${num} ${name}` : name}
                      </option>
                    );
                  })}
                  </select>
                </div>
              {eventType?.needsSecond ? (
                <div className="st-field">
                  <label>
                    {t(eventType.secondLabelKey || 'gmSecondPlayer')}
                    {Number(eventEditor.event_id) === 15 ? <span className="st-required"> *</span> : null}
                  </label>
                  <select className="st-select" value={eventEditor.additional_player_id} onChange={(e) => setEventEditor((prev) => ({ ...prev, additional_player_id: e.target.value }))}>
                      <option value="">
                        {Number(eventEditor.event_id) === 15 ? t('olmSelectPlayer') : t('mrNoAssist')}
                      </option>
                    {eventTeamPlayers(eventEditor.team_id, eventEditor.additional_player_id)
                      .filter((player) => Number(player.id) !== Number(eventEditor.player_id))
                      .map((player) => {
                        const num = playerShirtNumber(player);
                        const name = playerDisplayName(player);
                        return (
                          <option key={player.id} value={player.id}>
                            {num ? `#${num} ${name}` : name}
                          </option>
                        );
                      })}
                    </select>
                  </div>
              ) : null}
              <div className="st-field">
                <label>{t('gmEventTime')} <span className="st-required">*</span></label>
                <div className="gm-time-row">
                  <div className="gm-time-input">
                    <input
                      className="st-input gm-time-part"
                      maxLength={3}
                      inputMode="numeric"
                      required
                      placeholder="12"
                      aria-label={t('gmMinute')}
                      value={eventEditor.minute}
                      onChange={(e) => setEventEditor((prev) => ({ ...prev, minute: e.target.value.replace(/[^0-9]/g, '').slice(0, 3) }))}
                    />
                    <span className="gm-time-sep">'</span>
                    <input
                      className="st-input gm-time-part"
                      maxLength={2}
                      inputMode="numeric"
                      placeholder="00"
                      aria-label={t('gmSecond')}
                      value={eventEditor.second}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/[^0-9]/g, '').slice(0, 2);
                        setEventEditor((prev) => ({
                          ...prev,
                          second: digits === '' ? '' : (Number(digits) > 59 ? '59' : digits),
                        }));
                      }}
                    />
                  </div>
                  {onlineLive ? (
                    <OnlineClockNowButton
                      matchId={matchId}
                      onPick={(seconds, clock) => setEventEditor((prev) => ({ ...prev, ...onlineEventTime(seconds, clock) }))}
                    />
                  ) : estimateLiveEventClock(match) ? (
                    <button
                      type="button"
                      className="st-btn st-btn-ghost gm-mini-btn"
                      onClick={() => setEventEditor((prev) => ({ ...prev, ...liveEventTime() }))}
                    >
                      {t('gmUseLiveClock')}
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="st-field st-field-wide">
                <label>{t('comment')}</label>
                <input
                  className="st-input"
                  value={eventEditor.comment || ''}
                  onChange={(e) => setEventEditor((prev) => ({ ...prev, comment: e.target.value }))}
                />
                </div>
                </div>
          ) : null}
          <div className="gm-modal-actions">
            <button type="button" className="st-btn st-btn-ghost" onClick={() => setEventEditor(null)}>{t('cancel')}</button>
            {eventEditor?.id ? <button type="button" className="st-btn st-btn-danger" onClick={deleteEvent}>{t('delete')}</button> : null}
            <button
              type="button"
              className="st-btn st-btn-primary"
              disabled={!canSubmitEvent}
              onClick={saveEvent}
            >
              {t('save')}
              <kbd className="gm-quick-kbd gm-quick-kbd-on-btn">Enter</kbd>
            </button>
                  </div>
                </div>
      </CustomModal>

      <CustomModal className="gm-modal-host gm-staff-picker-host" visible={staffPickerOpen} closeModal={closeStaffPicker}>
        <div className="gm-modal">
          <h3>{refereePick ? t('msRefereePickTitle') : t('gmPickStaff')}</h3>
          {staffPickerError ? <div className="st-message error gm-staff-picker-error">{staffPickerError}</div> : null}
          {refereePick ? (
            <>
              <p className="gm-referee-pick-name">{staffDisplayName(refereePick.person)}</p>
              <div className="gm-referee-slots">
                {refereePick.slots.map((slot) => {
                  const option = REFEREE_SLOT_OPTIONS.find((item) => item.slot === slot);
                  if (!option) return null;
                  return (
                    <button
                      key={slot}
                      type="button"
                      className="gm-referee-slot"
                      onClick={() => commitStaffPerson(refereePick.person, slot)}
                    >
                      {t(option.labelKey)}
                    </button>
                  );
                })}
              </div>
              <button type="button" className="st-btn st-btn-ghost" onClick={() => setRefereePick(null)}>
                {t('cancel')}
              </button>
            </>
          ) : (
            <>
              <div className="gm-staff-picker-toolbar">
                <div className="st-search gm-staff-picker-search">
                  <FiSearch className="st-search-icon" />
                  <input
                    type="text"
                    className="st-search-input"
                    placeholder={t('gmStaffSearch')}
                    value={staffSearch}
                    onChange={(event) => setStaffSearch(event.target.value)}
                  />
                  {staffSearch ? (
                    <button type="button" className="st-search-clear" onClick={() => setStaffSearch('')}>×</button>
                  ) : null}
                </div>
                <select
                  className="st-select gm-staff-role-select"
                  value={staffRoleFilter}
                  onChange={(event) => setStaffRoleFilter(event.target.value)}
                >
                  <option value="">{t('gmAllStaffRoles')}</option>
                  {staffRoleOptions.map((role) => (
                    <option key={role.id} value={role.id}>{role.label}</option>
                  ))}
                </select>
              </div>
              <div className="gm-picker-list">
                {filteredStaffCatalog.length ? filteredStaffCatalog.map((person) => (
                  <div key={person.rowKey || person.id} className="gm-picker-item" onClick={() => pickStaffPerson(person)}>
                    <img src={staffPhotoUrl(person) || defaultAvatar} alt="" />
                    <div>
                      <strong>{staffDisplayName(person)}</strong>
                      {person.login && person.login !== staffDisplayName(person) ? (
                        <div className="gm-staff-login">{person.login}</div>
                      ) : null}
                      <div className="gm-muted">{person.position || roleLabel(t, person.role)}</div>
                    </div>
                  </div>
                )) : (
                  <div className="gm-picker-empty">{t('gmStaffEmpty')}</div>
                )}
              </div>
            </>
          )}
        </div>
      </CustomModal>

        <OnlineMatch
        visible={onlineOpen}
        closeModal={() => {
          setOnlineOpen(false);
          loadPage();
        }}
        matchData={{
          ...match,
          home_team_name: match.home_team?.name,
          guest_team_name: match.guest_team?.name,
          home_team_image: match.home_team?.image,
          guest_team_image: match.guest_team?.image,
        }}
        matchApplication={roster}
        />
    </div>
  );
}
